/* assets/gcitymap-platform.js — GPROPERTY-MAP-RUNTIME-13
   Canonical runtime owner for verified address search, Leaflet rendering,
   selected-place persistence, optional geolocation, nearby context and responsive map UI.
   Includes built-in default open datasets: OpenStreetMap base tiles, MTR Entrances,
   EDB Schools, LCSD Parks, and Public Clinics.
   No mock place, image, overlay, distance, score or recommendation is generated. */
(function gpropertyMapRuntime(window, document) {
  'use strict';

  var VERSION = 'gproperty-map-runtime-15';
  var CONFIG_SELECTOR = '[data-gproperty-map-config]';
  var LAYER_SELECTOR = '[data-gproperty-map-layer-config]';
  var SEARCH_SELECTOR = '[data-gproperty-map-search-root]';
  var WORKSPACE_SELECTOR = '[data-gproperty-map-workspace]';
  var HEADER_SELECTOR = '[data-map-surface]';
  var DEFAULT_CENTER = [22.3193, 114.1694];
  var DEFAULT_ZOOM = 12;
  var OSM_PROVIDER = 'openstreetmap-standard';
  var OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  var OSM_ATTRIBUTION = '© OpenStreetMap contributors';
  var MOBILE_QUERY = '(max-width: 767px)';
  var SHEET_STATES = ['collapsed', 'half', 'expanded'];
  var HISTORY_KEY = 'gproperty-map-search-history';
  var SAVED_KEY = 'gproperty-map-saved-places';
  var MAX_HISTORY = 8;
  var DEFAULT_LAYERS = [
    {
      id: 'mtr',
      labelZh: '港鐵出入口',
      labelEn: 'MTR Station Entrances',
      nearbyType: 'mtr',
      apiFamily: 'nearby',
      renderMode: 'nearby-list-map',
      mapOverlay: true,
      visibleByDefault: false,
      availableOn: ['free', 'pro'],
    },
    {
      id: 'school',
      labelZh: '教育局學校名冊',
      labelEn: 'EDB Schools & Education',
      nearbyType: 'school',
      apiFamily: 'nearby',
      renderMode: 'nearby-list-map',
      mapOverlay: true,
      visibleByDefault: false,
      availableOn: ['free', 'pro'],
    },
    {
      id: 'park',
      labelZh: '康文署公園及休閒設施',
      labelEn: 'LCSD Parks & Recreation',
      nearbyType: 'park',
      apiFamily: 'nearby',
      renderMode: 'nearby-list-map',
      mapOverlay: true,
      visibleByDefault: false,
      availableOn: ['free', 'pro'],
    },
    {
      id: 'clinic',
      labelZh: '公營診所及醫療機構',
      labelEn: 'Public Clinics & Medical',
      nearbyType: 'clinic',
      apiFamily: 'nearby',
      renderMode: 'nearby-list-map',
      mapOverlay: true,
      visibleByDefault: false,
      availableOn: ['free', 'pro'],
    },
    {
      id: 'price',
      labelZh: '物業價格',
      labelEn: 'Property prices',
      nearbyType: '',
      apiFamily: 'public-listings',
      renderMode: 'price-marker-cluster',
      mapOverlay: true,
      visibleByDefault: false,
      availableOn: ['free', 'pro'],
    },
  ];

  if (window.__GPROPERTY_MAP_RUNTIME__ === VERSION) return;
  window.__GPROPERTY_MAP_RUNTIME__ = VERSION;

  function one(root, selector) {
    return (root || document).querySelector(selector);
  }
  function all(root, selector) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }
  function clean(value, limit) {
    return String(value == null ? '' : value)
      .replace(/[<>"'`]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, limit || 240);
  }
  function finite(value) {
    if (value === '' || value == null) return null;
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }
  function validCoordinates(lat, lng) {
    return lat !== null && lng !== null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }
  function bool(value) {
    return value === true || clean(value, 10).toLowerCase() === 'true';
  }

  function contractBool(node, key, fallback) {
    if (!node || !node.dataset || !Object.prototype.hasOwnProperty.call(node.dataset, key)) {
      return fallback;
    }

    return bool(node.dataset[key]);
  }
  function setText(node, value, fallback) {
    if (node) node.textContent = clean(value || fallback || '');
  }
  function setHidden(node, hidden) {
    if (node) node.hidden = !!hidden;
  }
  function isMobile() {
    try {
      return !!(window.matchMedia && window.matchMedia(MOBILE_QUERY).matches);
    } catch (_) {
      return window.innerWidth <= 767;
    }
  }
  function hasCoarsePointer() {
    try {
      return (
        Number(navigator.maxTouchPoints || 0) > 0 ||
        !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
      );
    } catch (_) {
      return Number(navigator.maxTouchPoints || 0) > 0;
    }
  }
  function isNative() {
    try {
      return !!(
        window.Capacitor &&
        typeof window.Capacitor.isNativePlatform === 'function' &&
        window.Capacitor.isNativePlatform()
      );
    } catch (_) {
      return false;
    }
  }
  function safeUrl(value) {
    var raw = clean(value, 900);
    if (!raw) return '';
    try {
      var url = new URL(raw, window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch (_) {
      return '';
    }
  }

  function fallbackRegistry() {
    var byType = {};
    DEFAULT_LAYERS.forEach(function (layer) {
      byType[layer.nearbyType] = layer;
    });
    return {
      role: 'default',
      surfaceTier: 'pro',
      valid: true,
      rendersMapOverlay: true,
      byType: byType,
      layers: DEFAULT_LAYERS,
      policy: {
        priority: 'free-no-registration-first',
        frontendApiKeys: false,
        registrationRequired: false,
        paidProviderDefault: false,
        backendAdapterRequired: true,
        offlineTilePrefetch: false,
        bulkTileDownload: false,
      },
      baseMap: {
        providerId: OSM_PROVIDER,
        urlTemplate: OSM_URL,
        attribution: OSM_ATTRIBUTION,
        maxZoom: 19,
        apiKeyRequired: false,
        registrationRequired: false,
        offlinePrefetch: false,
        bulkDownload: false,
        detectRetina: false,
        updateWhenIdle: true,
        keepBuffer: 2,
      },
    };
  }

  function readRegistry() {
    var fallback = fallbackRegistry();
    var node = one(document, LAYER_SELECTOR);
    var headerNode = one(document, HEADER_SELECTOR);

    // 優先從 Header 或 URL 取得當前的 surfaceTier (free 或 pro)
    var headerTier = headerNode ? clean(headerNode.dataset.mapSurface, 20).toLowerCase() : '';
    if (!headerNode) {
      try {
        var params = new URLSearchParams(window.location.search);
        headerTier = clean(params.get('tier') || params.get('surface'), 20).toLowerCase();
      } catch (_) {}
    }

    if (!node) {
      if (headerTier === 'free' || headerTier === 'pro') fallback.surfaceTier = headerTier;
      return fallback;
    }

    try {
      var payload = JSON.parse(node.textContent || '{}'),
        policy = payload.provider_policy || {},
        map = payload.base_map || {},
        byType = {},
        layers = [];

      var surfaceTier =
        headerTier === 'free' || headerTier === 'pro'
          ? headerTier
          : clean(payload.surface_tier, 20).toLowerCase() === 'free'
            ? 'free'
            : 'pro';

      var rawLayers =
        Array.isArray(payload.layers) && payload.layers.length ? payload.layers : DEFAULT_LAYERS;

      rawLayers.forEach(function (raw) {
        if (!raw || typeof raw !== 'object') return;

        var availableOn = Array.isArray(raw.available_on)
          ? raw.available_on
              .map(function (value) {
                return clean(value, 20).toLowerCase();
              })
              .filter(function (value, index, values) {
                return (value === 'free' || value === 'pro') && values.indexOf(value) === index;
              })
          : ['free', 'pro'];

        if (availableOn.indexOf(surfaceTier) === -1) return;

        var type = clean(raw.nearby_type, 40),
          layer = {
            id: clean(raw.id || type, 40),
            labelZh: clean(raw.label_zh || raw.id || type, 80),
            labelEn: clean(raw.label_en || raw.id || type, 80),
            nearbyType: type,
            apiFamily: clean(raw.api_family, 40) || 'nearby',
            renderMode: clean(raw.render_mode, 40) || 'nearby-list',
            mapOverlay: raw.map_overlay === true,
            visibleByDefault: raw.visible_by_default === true,
            availableOn: availableOn,
          };
        layers.push(layer);
        if (type) byType[type] = layer;
      });

      var normalizedPolicy = {
        priority: clean(policy.priority, 80) || 'free-no-registration-first',
        frontendApiKeys: bool(policy.frontend_api_keys),
        registrationRequired: bool(policy.registration_required),
        paidProviderDefault: bool(policy.paid_provider_default),
        backendAdapterRequired: policy.backend_adapter_required_for_data_apis !== false,
        offlineTilePrefetch: bool(policy.offline_tile_prefetch),
        bulkTileDownload: bool(policy.bulk_tile_download),
      };

      var baseMap = {
        providerId: clean(map.provider_id, 80) || OSM_PROVIDER,
        urlTemplate: clean(map.url_template, 500) || OSM_URL,
        attribution: clean(map.attribution, 180) || OSM_ATTRIBUTION,
        maxZoom: Math.min(22, Math.max(1, Math.round(Number(map.max_zoom) || 19))),
        apiKeyRequired: bool(map.api_key_required),
        registrationRequired: bool(map.registration_required),
        offlinePrefetch: bool(map.offline_prefetch),
        bulkDownload: bool(map.bulk_download),
        detectRetina: bool(map.detect_retina),
        updateWhenIdle: map.update_when_idle !== false,
        keepBuffer: Math.min(4, Math.max(0, Math.round(Number(map.keep_buffer) || 2))),
      };

      var valid =
        normalizedPolicy.priority === 'free-no-registration-first' &&
        !normalizedPolicy.frontendApiKeys &&
        !normalizedPolicy.registrationRequired &&
        !normalizedPolicy.paidProviderDefault &&
        normalizedPolicy.backendAdapterRequired &&
        !normalizedPolicy.offlineTilePrefetch &&
        !normalizedPolicy.bulkTileDownload &&
        baseMap.providerId === OSM_PROVIDER &&
        baseMap.urlTemplate === OSM_URL &&
        baseMap.attribution.indexOf('OpenStreetMap') !== -1 &&
        !baseMap.apiKeyRequired &&
        !baseMap.registrationRequired &&
        !baseMap.offlinePrefetch &&
        !baseMap.bulkDownload;

      return {
        role: clean(payload.contract_role, 80) || fallback.role,
        surfaceTier: surfaceTier,
        valid: valid,
        rendersMapOverlay: payload.renders_map_overlay === true,
        byType: byType,
        layers: layers,
        policy: valid ? normalizedPolicy : fallback.policy,
        baseMap: valid ? baseMap : fallback.baseMap,
      };
    } catch (_) {
      return fallback;
    }
  }

  function readConfig() {
    var node = one(document, CONFIG_SELECTOR);
    if (!node) return null;
    var base = clean(node.dataset.gpropertyApiBase, 300).replace(/\/+$/, ''),
      registry = readRegistry();
    var providerValid =
      clean(node.dataset.providerPriorityPolicy, 80) === 'free-no-registration-first' &&
      !bool(node.dataset.frontendApiKeys) &&
      !bool(node.dataset.providerRegistrationRequired) &&
      !bool(node.dataset.providerPaidDefault) &&
      clean(node.dataset.baseMapProvider, 80) === OSM_PROVIDER &&
      clean(node.dataset.baseMapUrlTemplate, 500) === OSM_URL &&
      clean(node.dataset.baseMapAttribution, 180).indexOf('OpenStreetMap') !== -1 &&
      !bool(node.dataset.baseMapApiKeyRequired) &&
      !bool(node.dataset.baseMapRegistrationRequired) &&
      !bool(node.dataset.baseMapOfflinePrefetch) &&
      clean(node.dataset.geocoderFallback, 80) === 'nominatim-backend-only' &&
      !bool(node.dataset.geocoderFallbackFrontendDirect) &&
      registry.valid;
    return {
      node: node,
      policy: 'gproperty-direct',
      base: base,

      locationSearch: base + clean(node.dataset.locationSearchPath, 120),

      nearby: base + clean(node.dataset.nearbyPath, 120),

      mapSearch: clean(node.dataset.mapSearchPath, 120)
        ? base + clean(node.dataset.mapSearchPath, 120)
        : '',

      schoolNet: clean(node.dataset.schoolNetPath, 120)
        ? base + clean(node.dataset.schoolNetPath, 120)
        : '',

      localDraftKey: clean(node.dataset.localDraftKey, 120) || 'gproperty_spatial_brief_draft_v2',

      familyFitUrl: clean(node.dataset.familyFitUrl, 400),

      ownerFitUrl: clean(node.dataset.ownerFitUrl, 400),

      assetsUrl: clean(node.dataset.assetsUrl, 400),

      reportUrl: clean(node.dataset.reportUrl, 400),

      nearbyRegistry: registry,

      providerPolicy: providerValid ? registry.policy : fallbackRegistry().policy,

      baseMap: providerValid ? registry.baseMap : fallbackRegistry().baseMap,

      providerContractValid: providerValid,

      mapSelectionRead: contractBool(node, 'mapSelectionRead', true),

      mapSelectionWrite: contractBool(node, 'mapSelectionWrite', true),

      searchHistoryRead: contractBool(node, 'searchHistoryRead', true),

      searchHistoryWrite: contractBool(node, 'searchHistoryWrite', true),

      savedPlaceRead: contractBool(node, 'savedPlaceRead', true),

      savedPlaceWrite: contractBool(node, 'savedPlaceWrite', true),

      eventPayloadPolicy:
        clean(node.dataset.eventPayloadPolicy, 40) === 'minimal' ? 'minimal' : 'full',
    };
  }

  function emitMapEvent(config, eventName, fullDetail, minimalDetail) {
    var detail =
      config && config.eventPayloadPolicy === 'minimal'
        ? minimalDetail && typeof minimalDetail === 'object'
          ? minimalDetail
          : {}
        : fullDetail && typeof fullDetail === 'object'
          ? fullDetail
          : {};

    try {
      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: detail,
        })
      );

      return true;
    } catch (_) {
      return false;
    }
  }

  function absoluteUrl(path) {
    var origin =
      window.location.origin && window.location.origin !== 'null'
        ? window.location.origin
        : 'https://glhksolution.com';
    return new URL(path, origin);
  }
  function apiRequestError(response, body) {
    var code = clean(body && body.error, 120) || 'http_' + response.status,
      error = new Error(code);
    error.code = code;
    error.httpStatus = Number(response.status || 0);
    error.apiStatus = clean(body && body.status, 80);
    error.apiData = body && body.data && typeof body.data === 'object' ? body.data : null;
    return error;
  }
  function requestJson(url, requestState, timeoutMs) {
    if (!navigator.onLine) {
      var offlineError = new Error('offline');
      offlineError.code = 'offline';
      offlineError.httpStatus = 0;
      offlineError.apiStatus = 'offline';
      return Promise.reject(offlineError);
    }
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (requestState && requestState.controller) requestState.controller.abort();
    if (requestState) requestState.controller = controller;

    var timeout = window.setTimeout(function () {
      if (requestState) requestState.timedOut = true;
      if (controller) controller.abort();
    }, timeoutMs || 15000);

    return window
      .fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined,
      })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (body) {
            if (!response.ok) throw apiRequestError(response, body);
            var payload =
              body && typeof body.data === 'object' && body.data !== null ? body.data : body;
            var hasValidItems =
              payload && (Array.isArray(payload.items) || Array.isArray(payload.results));
            var isOk =
              body &&
              (body.success === true ||
                body.ok === true ||
                body.status === 'success' ||
                hasValidItems);
            if (!isOk) {
              var invalid = new Error('invalid_response_contract');
              invalid.code = 'invalid_response_contract';
              invalid.httpStatus = Number(response.status || 0);
              invalid.apiStatus = clean(body && body.status, 80);
              throw invalid;
            }
            return payload;
          });
      })
      .finally(function () {
        window.clearTimeout(timeout);
        if (requestState) requestState.controller = null;
      });
  }

  function hashKey(value) {
    var text = clean(value, 500),
      hash = 2166136261;
    for (var index = 0; index < text.length; index += 1) {
      hash ^= text.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return 'loc_' + (hash >>> 0).toString(36);
  }
  function normalizePlace(raw, sourceFallback) {
    var item = raw && typeof raw === 'object' ? raw : {},
      lat = finite(item.lat != null ? item.lat : item.latitude),
      lng = finite(item.lng != null ? item.lng : item.longitude);
    var nameZh = clean(item.name_zh || item.name || item.label, 160),
      nameEn = clean(item.name_en, 160),
      address = clean(
        item.address_zh || item.formatted_address_zh || item.address || item.formatted_address,
        240
      );
    if (!nameZh || !validCoordinates(lat, lng)) return null;
    var estateId = clean(item.estate_id || item.place_id || item.id, 120);
    return {
      key: estateId || hashKey(nameZh + '|' + address + '|' + clean(item.source_ref, 160)),
      estate_id: estateId,
      name_zh: nameZh,
      name_en: nameEn,
      address: address,
      district_code: clean(
        item.district_code || item.district_zh || item.district_en || item.district,
        80
      ),
      lat: lat,
      lng: lng,
      source: clean(item.source || item.provider || sourceFallback || 'gproperty-api', 80),
      source_ref: clean(item.source_ref, 160),
      updated_at: clean(item.updated_at || item.source_updated_at || item.retrieved_at, 80),
      dataset_version: clean(item.dataset_version, 80),
      confidence: finite(item.confidence != null ? item.confidence : item.match_score),
      price_label: clean(item.price_label || item.price_display || item.price, 40),
      image_url: safeUrl(item.image_url || item.thumbnail_url || item.hero_image_url),
      gallery_url: safeUrl(item.gallery_url || item.photos_url),
      transit_time: clean(item.transit_time_label || item.transit_time, 40),
    };
  }
  function normalizePlaces(data) {
    var rows = Array.isArray(data.items)
      ? data.items
      : Array.isArray(data.results)
        ? data.results
        : [];
    return rows
      .map(function (row) {
        return normalizePlace(row, 'gproperty-api');
      })
      .filter(Boolean)
      .slice(0, 100);
  }
  function toRadians(value) {
    return (Number(value) * Math.PI) / 180;
  }
  function haversineDistanceM(originLat, originLng, targetLat, targetLng) {
    if (!validCoordinates(originLat, originLng) || !validCoordinates(targetLat, targetLng))
      return null;
    var dLat = toRadians(targetLat - originLat),
      dLng = toRadians(targetLng - originLng),
      lat1 = toRadians(originLat),
      lat2 = toRadians(targetLat);
    var sinLat = Math.sin(dLat / 2),
      sinLng = Math.sin(dLng / 2);
    var a = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
    return Math.round(6371008.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(Math.max(0, 1 - a))));
  }
  function normalizeNearbyFeature(raw, type, originPlace) {
    var item = raw && typeof raw === 'object' ? raw : {};
    var lat = finite(item.lat != null ? item.lat : item.latitude),
      lng = finite(item.lng != null ? item.lng : item.longitude);
    var nameZh = clean(item.name_zh || item.name || item.label || item.title, 160),
      nameEn = clean(item.name_en || item.title_en, 160);
    if (!nameZh || !validCoordinates(lat, lng)) return null;
    var originLat = finite(originPlace && originPlace.lat),
      originLng = finite(originPlace && originPlace.lng);
    var sourceRef = clean(item.source_ref || item.reference || item.url, 160);
    return {
      id: clean(
        item.id || item.place_id || sourceRef || hashKey(nameZh + '|' + lat + '|' + lng),
        160
      ),
      type: clean(type || item.type || item.category, 40),
      title: nameZh,
      name_zh: nameZh,
      name_en: nameEn,
      lat: lat,
      lng: lng,
      distance_m: validCoordinates(originLat, originLng)
        ? haversineDistanceM(originLat, originLng, lat, lng)
        : null,
      source: clean(item.source || item.provider || item.source_provider, 80),
      source_ref: sourceRef,
      updated_at: clean(item.updated_at || item.source_updated_at || item.retrieved_at, 80),
    };
  }

  function updateSearchState(root, state, status, error) {
    if (!root) return;
    root.dataset.state = state;
    root.classList.toggle('is-loading', state === 'loading');
    root.classList.toggle('is-offline', state === 'offline');
    var statusNode =
      one(root, '[data-gproperty-map-search-status-text]') ||
      one(root, '[data-gproperty-map-search-status]');
    setText(statusNode, status);
    var errorNode = one(root, '[data-gproperty-map-search-error]');
    setText(errorNode, error);
    setHidden(errorNode, !error);
  }
  function setQueryState(root, value) {
    var length = clean(value, 120).length;
    var state = length === 0 ? 'empty' : length < 2 ? 'short' : 'ready';
    if (root) root.dataset.queryState = state;
    return state;
  }
  function updateWorkspaceState(root, state, status, error) {
    if (!(root instanceof HTMLElement)) return;
    root.dataset.state = state;
    root.classList.toggle('is-loading', state === 'loading');
    root.classList.toggle('is-offline', state === 'offline');
    var kidContext = mapboxRendererContext(root);
    if (kidContext && kidContext.root) {
      kidContext.root.setAttribute('data-kid-loading', state === 'loading' ? 'true' : 'false');
    }
    setText(one(root, '[data-gproperty-map-status]'), status);
    var errorNode = one(root, '[data-gproperty-map-error]');
    setText(errorNode, error);
    setHidden(errorNode, !error);
  }

  function projectKidSearchNetworkState(workspace, state) {
    var context = mapboxRendererContext(workspace);
    if (!context || !context.root) return false;
    context.root.setAttribute('data-kid-search-network-state', clean(state, 24) || 'online');
    return true;
  }

  function readSearchHistory() {
    var config = window.__gpropertyMapConfig;

    if (config && config.searchHistoryRead === false) {
      return [];
    }

    try {
      var raw = JSON.parse(window.localStorage.getItem(HISTORY_KEY) || '[]');

      return Array.isArray(raw)
        ? raw
            .map(function (item) {
              return clean(item, 120);
            })
            .filter(Boolean)
            .slice(0, MAX_HISTORY)
        : [];
    } catch (_) {
      return [];
    }
  }

  function saveSearchHistory(term) {
    var config = window.__gpropertyMapConfig;

    if (config && config.searchHistoryWrite === false) {
      return false;
    }

    var value = clean(term, 120);
    if (!value) return false;

    var next = [value]
      .concat(
        readSearchHistory().filter(function (item) {
          return item.toLowerCase() !== value.toLowerCase();
        })
      )
      .slice(0, MAX_HISTORY);

    try {
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      return true;
    } catch (_) {
      return false;
    }
  }

  function closeSuggestions(searchRoot) {
    if (!searchRoot) return;
    var input = one(searchRoot, '[data-gproperty-map-search-input]'),
      list = one(searchRoot, '[data-gproperty-map-suggestions]');
    if (input) {
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-activedescendant', '');
    }
    setHidden(list, true);
    searchRoot.__suggestionIndex = -1;
  }
  function renderHistory(searchRoot) {
    var list = one(searchRoot, '[data-gproperty-map-suggestions]'),
      input = one(searchRoot, '[data-gproperty-map-search-input]');
    if (!list || !input) return;
    var history = readSearchHistory();
    list.replaceChildren();
    searchRoot.__suggestions = [];
    searchRoot.__suggestionIndex = -1;
    history.forEach(function (term) {
      var li = document.createElement('li'),
        button = document.createElement('button'),
        title = document.createElement('strong'),
        detail = document.createElement('small');
      button.type = 'button';
      button.className = 'gproperty-map-search__history-item';
      button.dataset.gpropertyMapHistoryTerm = term;
      button.setAttribute('role', 'option');
      title.textContent = term;
      detail.textContent =
        document.documentElement.lang.indexOf('zh') === 0 ? '最近搜尋' : 'Recent search';
      button.appendChild(title);
      button.appendChild(detail);
      li.appendChild(button);
      list.appendChild(li);
    });
    setHidden(list, !history.length);
    input.setAttribute('aria-expanded', history.length ? 'true' : 'false');
  }
  function renderSuggestions(searchRoot, places) {
    var list = one(searchRoot, '[data-gproperty-map-suggestions]'),
      input = one(searchRoot, '[data-gproperty-map-search-input]');
    if (!list || !input) return;
    list.replaceChildren();
    searchRoot.__suggestions = places.slice(0, 8);
    searchRoot.__suggestionIndex = -1;
    searchRoot.__suggestions.forEach(function (place, index) {
      var li = document.createElement('li'),
        button = document.createElement('button'),
        title = document.createElement('strong'),
        detail = document.createElement('small');
      button.type = 'button';
      button.id = searchRoot.id + '-suggestion-' + index;
      button.className = 'gproperty-map-search__suggestion';
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', 'false');
      button.dataset.gpropertyMapSuggestionIndex = String(index);
      title.textContent = place.name_zh + (place.name_en ? ' · ' + place.name_en : '');
      detail.textContent = place.address || place.district_code || place.source;
      button.appendChild(title);
      button.appendChild(detail);
      li.appendChild(button);
      list.appendChild(li);
    });
    setHidden(list, !list.children.length);
    input.setAttribute('aria-expanded', list.children.length ? 'true' : 'false');
  }
  function moveSuggestion(searchRoot, index) {
    var input = one(searchRoot, '[data-gproperty-map-search-input]'),
      buttons = all(searchRoot, '[data-gproperty-map-suggestion-index]');
    if (!input || !buttons.length) return;
    if (index < 0) index = buttons.length - 1;
    if (index >= buttons.length) index = 0;
    buttons.forEach(function (button, position) {
      var active = position === index;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) input.setAttribute('aria-activedescendant', button.id);
    });
    searchRoot.__suggestionIndex = index;
  }

  function markerLayer(map) {
    if (window.L && typeof window.L.markerClusterGroup === 'function')
      return window.L.markerClusterGroup({
        showCoverageOnHover: false,
        maxClusterRadius: 48,
      }).addTo(map);
    return window.L.layerGroup().addTo(map);
  }
  // Verified public listings share this Leaflet map, never the Kid Map renderer.
  function normalizePublicPriceListing(raw) {
    var item = raw && typeof raw === 'object' ? raw : {};
    var id = clean(item.property_id, 120);
    var lat = finite(item.lat),
      lng = finite(item.lng),
      price = finite(item.price_hkd);
    var type = clean(item.transaction_type, 12).toLowerCase();
    var source = clean(item.source, 80),
      ref = clean(item.source_ref, 160);
    var updated = clean(item.updated_at, 60);
    if (
      !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,119}$/.test(id) ||
      !validCoordinates(lat, lng) ||
      price === null ||
      price <= 0 ||
      (type !== 'sale' && type !== 'rent') ||
      clean(item.price_type, 20) !== 'asking' ||
      clean(item.listing_status, 20) !== 'active' ||
      clean(item.currency, 8) !== 'HKD' ||
      item.visibility !== 'public' ||
      item.public_searchable !== true ||
      item.coordinates_public !== true ||
      ['estate', 'building', 'station'].indexOf(clean(item.location_precision, 20)) < 0 ||
      !source ||
      !ref ||
      !/^\d{4}-\d{2}-\d{2}T/.test(updated) ||
      !Number.isFinite(Date.parse(updated)) ||
      Date.parse(updated) > Date.now() + 86400000
    )
      return null;
    var amount =
      price >= 1000000
        ? String(Math.round(price / 1000) / 10) + '萬'
        : Math.round(price).toLocaleString('en-HK');
    var label = 'HK$' + amount + (type === 'rent' ? '/月' : '');
    var place = normalizePlace(
      Object.assign({}, item, {
        id: id,
        name_zh: clean(item.title || item.estate_name || item.name_zh, 160),
        price_label: label,
      }),
      'public-listing'
    );
    if (!place) return null;
    place.key = 'listing:' + id;
    place.estate_id = clean(item.estate_id || item.place_id, 120);
    place.price_label = label;
    place.__isPublicPrice = true;
    return place;
  }

  function createPublicPriceLayer() {
    if (!window.L || typeof window.L.markerClusterGroup !== 'function') return null;
    return window.L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: function (zoom) {
        return zoom < 12 ? 64 : zoom < 15 ? 48 : 32;
      },
      iconCreateFunction: function (cluster) {
        var label = document.createElement('span');
        label.className = 'gproperty-price-cluster';
        label.textContent = String(cluster.getChildCount()) + ' 個物業';
        return window.L.divIcon({
          className: 'gproperty-price-cluster-shell',
          html: label.outerHTML,
          iconSize: [60, 44],
          iconAnchor: [30, 22],
        });
      },
    });
  }

  function syncPublicPriceSelection(workspace, key) {
    Object.keys(workspace.__publicPriceMarkers || {}).forEach(function (id) {
      var marker = workspace.__publicPriceMarkers[id];
      var icon = marker && marker.getElement && marker.getElement();
      var label = icon && icon.querySelector('.gproperty-price-marker');
      if (label) label.classList.toggle('is-active', 'listing:' + id === key);
    });
  }

  function setPublicPriceListings(rows) {
    var workspace = one(document, WORKSPACE_SELECTOR);
    if (
      !workspace ||
      !workspace.__publicPriceLayer ||
      (workspace.closest && workspace.closest('[data-littleg-kid-map]')) ||
      !Array.isArray(rows)
    )
      return 0;
    var layer = workspace.__publicPriceLayer;
    layer.clearLayers();
    workspace.__publicPriceMarkers = Object.create(null);
    var seen = Object.create(null),
      count = 0;
    rows.slice(0, 1000).forEach(function (raw) {
      var place = normalizePublicPriceListing(raw),
        id = clean(raw && raw.property_id, 120);
      if (!place || seen[id] || count >= 500) return;
      seen[id] = true;
      var label = document.createElement('span');
      label.className = 'gproperty-price-marker';
      label.textContent = place.price_label;
      var icon = window.L.divIcon({
        className: 'gproperty-price-marker-shell',
        html: label.outerHTML,
        iconSize: [1, 1],
        iconAnchor: [0, 0],
      });
      var marker = window.L.marker([place.lat, place.lng], {
        icon: icon,
        keyboard: true,
        riseOnHover: true,
        bubblingMouseEvents: false,
        title: place.name_zh + ' · ' + place.price_label,
      });
      marker.on('click', function (event) {
        if (event && event.originalEvent && window.L.DomEvent) {
          window.L.DomEvent.stopPropagation(event.originalEvent);
        }
        selectPlace(place);
      });
      layer.addLayer(marker);
      workspace.__publicPriceMarkers[id] = marker;
      count += 1;
    });
    workspace.dataset.publicPriceCount = String(count);
    syncPublicPriceLayerControl(workspace);
    var selected = workspace.__selectedPlace;
    if (selected && selected.__isPublicPrice) {
      if (!seen[selected.key.slice(8)]) clearSelectedPlace(workspace, window.__gpropertyMapConfig);
      else syncPublicPriceSelection(workspace, selected.key);
    }
    return count;
  }

  // The map instance is authoritative: do not mirror LayerGroup state in storage.
  function publicPriceLayerVisible(workspace) {
    return !!(
      workspace &&
      workspace.__map &&
      workspace.__publicPriceLayer &&
      typeof workspace.__map.hasLayer === 'function' &&
      workspace.__map.hasLayer(workspace.__publicPriceLayer)
    );
  }

  function syncPublicPriceLayerControl(workspace) {
    if (!workspace || (workspace.closest && workspace.closest('[data-littleg-kid-map]'))) return;
    var button = one(workspace, '[data-gproperty-map-layer-id="price"]');
    var count = Math.max(0, Number(workspace.dataset.publicPriceCount) || 0);
    var active = publicPriceLayerVisible(workspace);
    var chinese = document.documentElement.lang.indexOf('zh') === 0;
    if (button) {
      button.disabled = !workspace.__publicPriceLayer;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.classList.toggle('is-active', active);
      var note = one(button, 'small');
      if (note) {
        note.textContent = !workspace.__publicPriceLayer
          ? chinese
            ? '地圖載入中'
            : 'Map loading'
          : count
            ? chinese
              ? count + ' 個公開放盤'
              : count + ' public listings'
            : chinese
              ? '尚未接通公開樓盤資料'
              : 'Public listing feed not connected';
      }
    }
    var status = one(workspace, '[data-gproperty-map-price-layer-status]');
    if (status) {
      status.textContent =
        !count && active
          ? chinese
            ? '價格圖層已開啟，但尚未接通公開樓盤資料。'
            : 'Price layer is on, but no public listing feed is connected.'
          : '';
      setHidden(status, !active || count > 0);
    }
  }

  function togglePublicPriceLayer(workspace) {
    if (
      !workspace ||
      !workspace.__map ||
      !workspace.__publicPriceLayer ||
      (workspace.closest && workspace.closest('[data-littleg-kid-map]'))
    )
      return false;
    var current = publicPriceLayerVisible(workspace);
    if (current) workspace.__map.removeLayer(workspace.__publicPriceLayer);
    else workspace.__map.addLayer(workspace.__publicPriceLayer);
    syncPublicPriceLayerControl(workspace);
    return !current;
  }

  function showGestureHint(workspace, text) {
    var hint = one(workspace, '[data-gproperty-map-gesture-hint]');
    if (!hint) return;
    window.clearTimeout(workspace.__gestureHintTimer);
    setText(hint, text);
    setHidden(hint, false);
    hint.classList.add('is-visible');
    workspace.__gestureHintTimer = window.setTimeout(function () {
      hint.classList.remove('is-visible');
      setHidden(hint, true);
    }, 1500);
  }

  function mapboxRendererContext(workspace) {
    if (!workspace || workspace.__mapRenderer !== 'mapbox-gl') {
      return null;
    }

    var root = workspace.closest && workspace.closest('[data-littleg-kid-map]');

    var renderer = window.LittleGKidMapRenderer;

    return root && renderer
      ? {
          root: root,
          renderer: renderer,
        }
      : null;
  }

  function invalidateMap(workspace, delay) {
    if (!workspace || !workspace.__map) return;

    window.clearTimeout(workspace.__invalidateTimer);

    workspace.__invalidateTimer = window.setTimeout(
      function () {
        var context = mapboxRendererContext(workspace);

        if (context && typeof context.renderer.resize === 'function') {
          context.renderer.resize(context.root);
          return;
        }

        try {
          workspace.__map.invalidateSize({
            animate: false,
          });
        } catch (_) {}
      },
      delay == null ? 90 : delay
    );
  }
  function setResultsPanelState(workspace, nextState) {
    if (!workspace) return;
    var state = nextState === 'open' ? 'open' : 'collapsed';
    if (isMobile()) state = 'open';
    workspace.dataset.resultsPanelState = state;
    var toggle = one(workspace, '[data-gproperty-map-results-toggle]'),
      expanded = state === 'open';
    if (toggle) {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      toggle.setAttribute('aria-label', expanded ? '收起搜尋結果' : '展開搜尋結果');
      setText(
        one(toggle, '[data-gproperty-map-results-toggle-label]'),
        expanded ? '收起搜尋結果' : '展開搜尋結果'
      );
    }
    invalidateMap(workspace, 270);
  }
  function toggleResultsPanel(workspace) {
    setResultsPanelState(
      workspace,
      clean(workspace.dataset.resultsPanelState, 20) === 'open' ? 'collapsed' : 'open'
    );
  }

  function bindCooperativeGestures(workspace, map, canvas) {
    if (isNative()) {
      workspace.dataset.gestureMode = 'greedy';
      return;
    }
    workspace.dataset.gestureMode = 'cooperative';
    if (map.scrollWheelZoom) map.scrollWheelZoom.disable();
    canvas.addEventListener(
      'wheel',
      function (event) {
        if (event.ctrlKey || event.metaKey) {
          if (map.scrollWheelZoom) map.scrollWheelZoom.enable();
          window.clearTimeout(workspace.__wheelDisableTimer);
          workspace.__wheelDisableTimer = window.setTimeout(function () {
            if (map.scrollWheelZoom) map.scrollWheelZoom.disable();
          }, 500);
        } else {
          if (map.scrollWheelZoom) map.scrollWheelZoom.disable();
          showGestureHint(workspace, '按住 Ctrl 或 ⌘ 再滾輪縮放地圖');
        }
      },
      { capture: true, passive: true }
    );
    if (!hasCoarsePointer()) return;
    workspace.dataset.gestureMode = 'cooperative-touch';
    if (map.dragging) map.dragging.disable();
    function syncTouch(event, showHint) {
      var count = event && event.touches ? event.touches.length : 0;
      workspace.dataset.gestureTouchCount = String(count);
      if (count >= 2) {
        if (map.dragging) map.dragging.enable();
        return true;
      }
      if (map.dragging) map.dragging.disable();
      if (count === 1 && showHint) showGestureHint(workspace, '使用兩隻手指操作地圖');
      return false;
    }
    canvas.addEventListener(
      'touchstart',
      function (event) {
        syncTouch(event, true);
      },
      { capture: true, passive: true }
    );
    canvas.addEventListener(
      'touchmove',
      function (event) {
        if (!syncTouch(event, true)) event.stopPropagation();
      },
      { capture: true, passive: true }
    );
    canvas.addEventListener(
      'touchend',
      function (event) {
        syncTouch(event, false);
      },
      { capture: true, passive: true }
    );
    canvas.addEventListener(
      'touchcancel',
      function (event) {
        syncTouch(event, false);
      },
      { capture: true, passive: true }
    );
  }

  function setMapSearchDisabled(workspace) {
    all(workspace, '[data-gproperty-map-search-area]').forEach(function (button) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'false');
      setHidden(button, true);
    });
    all(workspace, '[data-gproperty-map-apply-filters]').forEach(function (button) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'false');
    });
  }
  function failClosedMapSearch(workspace) {
    setMapSearchDisabled(workspace);
    var message = '此區域搜尋尚未配置可核實資料來源；請使用上方文字搜尋。';
    updateWorkspaceState(workspace, 'unavailable', message, message);
    return Promise.resolve([]);
  }
  function bindViewportSearch(workspace, config, map) {
    var button = one(workspace, '[data-gproperty-map-search-area]'),
      startCenter = null;
    if (!button) return;
    map.on('dragstart', function () {
      startCenter = map.getCenter();
    });
    map.on('dragend', function () {
      if (!startCenter || !config.mapSearch) {
        setMapSearchDisabled(workspace);
        return;
      }
      if (map.getCenter().distanceTo(startCenter) > 200) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        setHidden(button, false);
      }
    });
  }

  function isMapInteractionTarget(target, rendererName) {
    if (!target || typeof target.closest !== 'function') {
      return false;
    }

    var selector =
      rendererName === 'mapbox-gl'
        ? '.mapboxgl-marker,.mapboxgl-ctrl'
        : '.leaflet-marker-icon,.leaflet-interactive,.leaflet-control';

    return !!target.closest(selector);
  }

  function bindBlankMapClick(workspace, config, map) {
    if (!workspace || !map || typeof map.on !== 'function') {
      return false;
    }

    if (workspace.__blankMapClickMap === map) {
      return true;
    }

    workspace.__blankMapClickMap = map;

    map.on('click', function (event) {
      // GP Spatial sketch takes temporary ownership of blank map clicks only while drawing.
      if (workspace.dataset.spatialDrawing === 'true') return;
      setLayerPanelOpen(workspace, false);

      var original = event && event.originalEvent;

      var target = original && original.target;

      if (isMapInteractionTarget(target, clean(workspace.__mapRenderer, 40) || 'leaflet')) {
        return;
      }

      clearSelectedPlace(workspace, config);

      workspace.dispatchEvent(
        new CustomEvent('gproperty:map:blank-click', {
          bubbles: true,
          detail: {
            renderer: clean(workspace.__mapRenderer, 40) || 'leaflet',
          },
        })
      );
    });

    return true;
  }

  function initMap(workspace, config) {
    var canvas = one(workspace, '[data-gproperty-map-canvas]');

    var skeleton = one(workspace, '[data-gproperty-map-skeleton]');

    var mapStatus = one(workspace, '[data-gproperty-map-map-status]');

    if (!canvas) {
      return false;
    }

    var rendererName = clean(config && config.node && config.node.dataset.mapRenderer, 40);

    if (rendererName === 'mapbox-gl') {
      var renderer = window.LittleGKidMapRenderer;

      var root = workspace.closest('[data-littleg-kid-map]') || workspace;

      if (!renderer || typeof renderer.mount !== 'function') {
        return false;
      }

      var map = renderer.mount(root);

      if (!map) {
        return false;
      }

      bindBlankMapClick(workspace, config, map);

      setHidden(skeleton, true);

      return true;
    }

    const presetProvider =
      config.node && config.node.getAttribute('data-route-role') === 'place-context'
        ? window.LittleGCityMissionMapScene
        : null;

    const viewportPreset =
      presetProvider && typeof presetProvider.getPreset === 'function'
        ? presetProvider.getPreset('hong-kong-overview')
        : null;

    const validPreset =
      viewportPreset &&
      Array.isArray(viewportPreset.center) &&
      viewportPreset.center.length === 2 &&
      validCoordinates(viewportPreset.center[0], viewportPreset.center[1]) &&
      Number.isInteger(viewportPreset.zoom) &&
      viewportPreset.zoom >= 0 &&
      viewportPreset.zoom <= config.baseMap.maxZoom;

    const initialCenter = validPreset ? viewportPreset.center.slice() : DEFAULT_CENTER;

    const initialZoom = validPreset ? viewportPreset.zoom : DEFAULT_ZOOM;

    var map = window.L.map(canvas, {
      zoomControl: false,
      attributionControl: true,
      preferCanvas: true,
      keyboard: true,
      dragging: true,
      touchZoom: true,
      doubleClickZoom: true,
      scrollWheelZoom: false,
      tap: true,
      tapTolerance: 15,
    }).setView(initialCenter, initialZoom);
    if (window.L.control && typeof window.L.control.zoom === 'function') {
      workspace.__zoomControl = window.L.control.zoom({ position: 'bottomright' }).addTo(map);
    }
    var tiles = window.L.tileLayer(config.baseMap.urlTemplate, {
      maxZoom: config.baseMap.maxZoom,
      attribution: config.baseMap.attribution,
      detectRetina: config.baseMap.detectRetina,
      updateWhenIdle: config.baseMap.updateWhenIdle,
      keepBuffer: config.baseMap.keepBuffer,
    }).addTo(map);
    tiles.on('tileerror', function () {
      setText(mapStatus, '底圖暫時未能完整載入；搜尋與文字結果仍可使用。');
      setHidden(mapStatus, false);
    });
    workspace.__map = map;
    workspace.__markerLayer = markerLayer(map);
    workspace.__publicPriceLayer =
      workspace.getAttribute('data-public-price-enabled') !== 'false' &&
      !workspace.closest('[data-littleg-kid-map]')
        ? createPublicPriceLayer()
        : null;
    workspace.__publicPriceMarkers = Object.create(null);
    workspace.dataset.publicPriceCount = '0';
    workspace.__contextLayers = {
      mtr: window.L.layerGroup().addTo(map),
      school: window.L.layerGroup().addTo(map),
      park: window.L.layerGroup().addTo(map),
      clinic: window.L.layerGroup().addTo(map),
    };
    workspace.__locationMarker = null;
    workspace.__accuracyCircle = null;
    workspace.__lastLocation = null;
    bindBlankMapClick(workspace, config, map);
    bindCooperativeGestures(workspace, map, canvas);
    bindViewportSearch(workspace, config, map);
    setText(mapStatus, '');
    setHidden(mapStatus, true);
    setHidden(skeleton, true);
    renderLayerSwitcher(workspace, config);
    invalidateMap(workspace, 120);
    // Non-invasive extension point for the canonical map measurement preview.
    workspace.dispatchEvent(new CustomEvent('gproperty:map:ready', {
      bubbles: true,
      detail: { renderer: 'leaflet' }
    }));
    return true;
  }

  function renderNearbyMarkers(workspace, type, rows) {
    if (!workspace || !workspace.__map) {
      return;
    }

    var rendererContext = mapboxRendererContext(workspace);

    if (rendererContext && typeof rendererContext.renderer.setNearbyPoints === 'function') {
      rendererContext.renderer.setNearbyPoints(rendererContext.root, {
        type: type,
        points: rows
          .map(function (row) {
            var lat = finite(row.lat != null ? row.lat : row.latitude);

            var lng = finite(row.lng != null ? row.lng : row.longitude);

            var name = clean(row.name_zh || row.name_en || row.name || row.label, 160);

            if (!validCoordinates(lat, lng) || !name) {
              return null;
            }

            return {
              lat: lat,
              lng: lng,
              label: name,
            };
          })
          .filter(Boolean),
      });

      return;
    }

    if (!workspace.__contextLayers || !workspace.__contextLayers[type] || !window.L) {
      return;
    }

    var layer = workspace.__contextLayers[type];

    try {
      layer.clearLayers();
    } catch (_) {}

    rows.forEach(function (row) {
      var lat = finite(row.lat != null ? row.lat : row.latitude);

      var lng = finite(row.lng != null ? row.lng : row.longitude);

      if (!validCoordinates(lat, lng)) {
        return;
      }

      var name = clean(row.name_zh || row.name_en || row.name || row.label, 160);

      if (!name) {
        return;
      }

      var marker = window.L.circleMarker([lat, lng], {
        radius: 6,
        weight: 2,
        fillOpacity: 0.85,
        keyboard: true,
      });

      marker.bindTooltip(name, {
        direction: 'top',
        opacity: 0.95,
      });

      marker.addTo(layer);
    });
  }

  function clearContextLayer(workspace, type) {
    if (!workspace) {
      return;
    }

    var rendererContext = mapboxRendererContext(workspace);

    if (rendererContext && typeof rendererContext.renderer.clearNearbyPoints === 'function') {
      rendererContext.renderer.clearNearbyPoints(rendererContext.root, type);
      return;
    }

    if (!workspace.__contextLayers || !workspace.__contextLayers[type]) {
      return;
    }

    try {
      workspace.__contextLayers[type].clearLayers();
    } catch (_) {}
  }

  function waitForMap(workspace, config) {
    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      var isKidMap = workspace.hasAttribute('data-littleg-kid-map');
      var failed = isKidMap && workspace.getAttribute('data-kid-canvas-state') === 'error';
      if (
        !failed &&
        !initMap(workspace, config) &&
        workspace.getAttribute('data-kid-canvas-state') !== 'error' &&
        attempts < 50
      )
        return;
      window.clearInterval(timer);
      if (workspace.__map) {
        applyUrlContext(workspace);
        return;
      }
      setHidden(one(workspace, '[data-gproperty-map-skeleton]'), true);
      var renderer = isKidMap && window.LittleGKidMapRenderer;
      if (renderer && typeof renderer.reportUnavailable === 'function') {
        renderer.reportUnavailable(workspace);
        return;
      }
      var status = one(workspace, '[data-gproperty-map-map-status]');
      if (isKidMap) {
        workspace.setAttribute('data-kid-canvas-state', 'error');
        var canvas = one(workspace, '[data-gproperty-map-canvas]');
        if (canvas) canvas.setAttribute('aria-busy', 'false');
      }
      var language = document.documentElement.lang || '';
      var message = isKidMap
        ? /^zh-CN/i.test(language)
          ? '地图暂时无法载入，仍可使用搜索。'
          : /^zh/i.test(language)
            ? '地圖暫時未能載入，仍可使用搜尋。'
            : 'Map unavailable. You can still search.'
        : '地圖資源未能載入；仍可使用文字搜尋。';
      setText(status, message);
      setHidden(status, false);
    }, 160);
  }

  function clearMarkers(workspace) {
    if (!workspace) return;

    var rendererContext = mapboxRendererContext(workspace);

    if (rendererContext && typeof rendererContext.renderer.clearSearchMarkers === 'function') {
      rendererContext.renderer.clearSearchMarkers(rendererContext.root);
      return;
    }

    if (workspace.__markerLayer) {
      try {
        workspace.__markerLayer.clearLayers();
      } catch (_) {}
    }
  }

  function createMarker(workspace, place, selected) {
    if (!window.L) return null;
    var el = document.createElement('span');
    el.className = 'gproperty-map-marker' + (selected ? ' is-active' : '');
    el.textContent = place.name_zh;
    var icon = window.L.divIcon({
      className: 'gproperty-map-marker-shell',
      html: el.outerHTML,
      iconSize: null,
      iconAnchor: [14, 14],
    });
    var marker = window.L.marker([place.lat, place.lng], {
      icon: icon,
      keyboard: true,
      riseOnHover: true,
      bubblingMouseEvents: false,
      title: place.name_zh,
    });
    marker.on('click', function (event) {
      if (event && event.originalEvent && window.L.DomEvent)
        window.L.DomEvent.stopPropagation(event.originalEvent);
      selectPlace(place);
    });
    return marker;
  }

  function addMarker(workspace, place, selected) {
    if (!workspace || !workspace.__map || !place) {
      return;
    }

    var rendererContext = mapboxRendererContext(workspace);

    if (rendererContext && typeof rendererContext.renderer.addSearchMarker === 'function') {
      rendererContext.renderer.addSearchMarker(rendererContext.root, {
        id: place.key || place.estate_id || String(place.lat) + ',' + String(place.lng),
        lat: place.lat,
        lng: place.lng,
        label: place.price_label || place.name_zh || place.name_en || '',
        ariaLabel: place.name_zh || place.name_en || '',
        selected: selected === true,
        onClick: function () {
          selectPlace(place);
        },
      });

      return;
    }

    if (!workspace.__markerLayer) {
      return;
    }

    var marker = createMarker(workspace, place, selected);

    if (marker) {
      workspace.__markerLayer.addLayer(marker);
    }
  }
  function renderResults(workspace, places, fitMap) {
    var list = one(workspace, '[data-gproperty-map-result-list]'),
      count = one(workspace, '[data-gproperty-map-result-count]'),
      empty = one(workspace, '[data-gproperty-map-empty]');
    if (!list) return;
    setResultsPanelState(workspace, 'open');
    list.replaceChildren();
    clearMarkers(workspace);
    workspace.__items = places;
    places.forEach(function (place, index) {
      var li = document.createElement('li'),
        button = document.createElement('button'),
        title = document.createElement('strong'),
        detail = document.createElement('small');
      button.type = 'button';
      button.className = 'gproperty-map-result-card';
      button.dataset.gpropertyMapResultIndex = String(index);
      button.setAttribute('aria-label', '選擇 ' + place.name_zh);
      button.setAttribute('aria-pressed', 'false');
      title.textContent = place.name_zh + (place.name_en ? ' · ' + place.name_en : '');
      detail.textContent = place.address || place.district_code || place.source;
      button.appendChild(title);
      button.appendChild(detail);
      li.appendChild(button);
      list.appendChild(li);
      addMarker(workspace, place, false);
    });
    setText(count, String(places.length));
    setHidden(empty, places.length > 0);

    if (fitMap && places.length && workspace.__map) {
      var rendererContext = mapboxRendererContext(workspace);

      if (rendererContext && typeof rendererContext.renderer.fitBounds === 'function') {
        rendererContext.renderer.fitBounds(rendererContext.root, {
          points: places.map(function (place) {
            return {
              lat: place.lat,
              lng: place.lng,
            };
          }),
          padding: 48,
          maxZoom: 16,
        });
      } else if (window.L) {
        var bounds = window.L.latLngBounds(
          places.map(function (place) {
            return [place.lat, place.lng];
          })
        );

        if (bounds.isValid()) {
          workspace.__map.fitBounds(bounds.pad(0.12), {
            maxZoom: 16,
          });
        }
      }
    }
  }

  function clearLocalDraftKey(config) {
    if (!config || !config.localDraftKey) {
      return;
    }

    try {
      window.localStorage.removeItem(config.localDraftKey);
    } catch (_) {}
  }

  function readDraft(config) {
    if (!config) return {};

    if (config.mapSelectionRead === false) {
      if (config.mapSelectionWrite === false) {
        clearLocalDraftKey(config);
      }

      return {};
    }

    var gate = window.GPROPERTY_DATA_GATE;

    if (gate && typeof gate.readPropertyDraft === 'function') {
      var stored = gate.readPropertyDraft(config.localDraftKey, gate.legacyDraftKey);

      return stored && stored.draft ? stored.draft : {};
    }

    try {
      var raw = window.localStorage.getItem(config.localDraftKey);

      var draft = raw ? JSON.parse(raw) : {};

      return draft && typeof draft === 'object' ? draft : {};
    } catch (_) {
      return {};
    }
  }

  function writeDraft(config, place) {
    if (!config || config.mapSelectionWrite === false) {
      return false;
    }

    var draft = readDraft(config);

    draft.version = 2;

    draft.source = clean(draft.source, 40) || 'gproperty';

    draft.updated_at = new Date().toISOString();

    draft.estate_context = {
      estate_id: place.estate_id || null,

      name_zh: place.name_zh,

      name_en: place.name_en || null,

      address: place.address || null,

      district_code: place.district_code || null,

      lat: place.lat,

      lng: place.lng,

      source: place.source,

      source_ref: place.source_ref || null,

      source_updated_at: place.updated_at || null,

      dataset_version: place.dataset_version || null,

      price_label: place.price_label || null,

      image_url: place.image_url || null,

      gallery_url: place.gallery_url || null,

      transit_time: place.transit_time || null,
    };

    var gate = window.GPROPERTY_DATA_GATE;

    if (gate && typeof gate.writePropertyDraft === 'function') {
      return gate.writePropertyDraft(config.localDraftKey, draft);
    }

    try {
      window.localStorage.setItem(config.localDraftKey, JSON.stringify(draft));

      return true;
    } catch (_) {
      return false;
    }
  }

  function clearDraftSelection(config) {
    if (!config) return;

    if (config.mapSelectionRead === false || config.mapSelectionWrite === false) {
      clearLocalDraftKey(config);
      return;
    }

    var draft = readDraft(config);

    if (
      !draft ||
      typeof draft !== 'object' ||
      !Object.prototype.hasOwnProperty.call(draft, 'estate_context')
    ) {
      return;
    }

    delete draft.estate_context;
    draft.updated_at = new Date().toISOString();

    var gate = window.GPROPERTY_DATA_GATE;

    if (gate && typeof gate.writePropertyDraft === 'function') {
      gate.writePropertyDraft(config.localDraftKey, draft);
      return;
    }

    try {
      window.localStorage.setItem(config.localDraftKey, JSON.stringify(draft));
    } catch (_) {}
  } // Browser-local favorites only. No account or Shopify synchronization.
  function savedPlaceText(zh, en) {
    return document.documentElement.lang.toLowerCase().startsWith('zh') ? zh : en;
  }

  function readSaved() {
    try {
      const raw = window.localStorage.getItem(SAVED_KEY);
      const rows = raw === null ? [] : JSON.parse(raw);

      if (!Array.isArray(rows)) return null;
      return rows;
    } catch (_) {
      return null;
    }
  }

  function isSavedPlace(place) {
    const rows = readSaved();
    if (rows === null) return null;

    const key = clean(place && place.key, 240);
    return rows.some((row) => row && row.key === key);
  }

  function updateSavedPlaceButton(button, saved) {
    if (!button) return;

    if (saved === null) {
      button.removeAttribute('aria-pressed');
      setText(
        one(button, 'span:last-child'),
        savedPlaceText('收藏讀取失敗', 'Favorites unavailable')
      );
      return;
    }

    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    setText(
      one(button, 'span:last-child'),
      saved
        ? savedPlaceText('已存於本機', 'Saved locally')
        : savedPlaceText('儲存於本機', 'Save locally')
    );
  }

  /*
   * 地點收藏：沿用現有 runtime 與本機儲存。
   * 不讀取 Shopify Customer ID，不宣稱已同步至帳戶。
   */
  function toggleSavedPlace(workspace) {
    if (!workspace) return;

    const place = workspace.__selectedPlace;
    const button = one(workspace, '[data-gproperty-map-action-save]');
    if (!place || !button) return;

    const key = clean(place.key, 240);
    const lat = finite(place.lat);
    const lng = finite(place.lng);
    const name = clean(place.name_zh || place.name_en, 240);

    if (!key || !name || !validCoordinates(lat, lng)) {
      updateWorkspaceState(workspace, 'error', '地點資料不完整，暫時無法儲存。', '');
      return;
    }

    // 直接讀取，避免讀取失敗時覆蓋原有收藏。
    try {
      const raw = window.localStorage.getItem(SAVED_KEY);
      const rows = raw === null ? [] : JSON.parse(raw);

      if (!Array.isArray(rows)) {
        throw new Error('Invalid saved-place data');
      }

      const exists = rows.some((row) => row && row.key === key);
      const remaining = rows.filter((row) => row && row.key !== key);

      // 不靜默刪除舊收藏。
      if (!exists && remaining.length >= 50) {
        updateWorkspaceState(workspace, 'idle', '本機收藏已達 50 個，請先移除部分收藏。', '');
        return;
      }

      const next = exists
        ? remaining
        : [
            {
              key,
              estate_id: clean(place.estate_id, 120),
              name_zh: name,
              address: clean(place.address, 500),
              lat,
              lng,
              saved_at: new Date().toISOString(),
            },
            ...remaining,
          ];

      // 寫入成功後才更新按鈕。
      window.localStorage.setItem(SAVED_KEY, JSON.stringify(next));

      button.setAttribute('aria-pressed', exists ? 'false' : 'true');
      setText(one(button, 'span:last-child'), exists ? '儲存' : '已儲存');

      updateWorkspaceState(
        workspace,
        'idle',
        exists ? '已移除本機收藏。' : '已儲存於此瀏覽器，尚未同步至帳戶。',
        ''
      );
    } catch (error) {
      console.error('[LittleG Maps] 本機收藏更新失敗', error);
      updateWorkspaceState(workspace, 'error', '無法更新本機收藏，請檢查瀏覽器儲存設定。', '');
    }
  }

  function setSheetContentState(content, visible) {
    if (!content) return;
    content.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (visible) content.removeAttribute('inert');
    else content.setAttribute('inert', '');
  }
  function setSheetState(workspace, nextState) {
    var sheet = one(workspace, '[data-gproperty-map-sheet]');
    if (!sheet || sheet.hidden) return;
    var state = SHEET_STATES.indexOf(nextState) >= 0 ? nextState : 'collapsed';
    if (!isMobile()) state = 'expanded';
    sheet.dataset.sheetState = state;
    workspace.dataset.sheetState = state;
    workspace.dataset.sheetVisible = 'true';
    var expanded = state !== 'collapsed',
      label =
        state === 'collapsed'
          ? '展開已選地點資料'
          : state === 'half'
            ? '顯示全部已選地點資料'
            : '收起已選地點資料';
    all(sheet, '[data-gproperty-map-sheet-toggle], [data-gproperty-map-sheet-step]').forEach(
      function (button) {
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        button.setAttribute('aria-label', label);
      }
    );
    setText(one(sheet, '[data-gproperty-map-sheet-toggle-label]'), label);
    setText(
      one(sheet, '[data-gproperty-map-sheet-step]'),
      state === 'collapsed' ? '查看詳情' : state === 'half' ? '顯示全部' : '收起'
    );
    setSheetContentState(one(sheet, '[data-gproperty-map-sheet-content]'), expanded || !isMobile());
    invalidateMap(workspace, 320);
  }
  function stepSheet(workspace, direction) {
    var current = clean(workspace.dataset.sheetState, 20) || 'collapsed',
      index = Math.max(0, SHEET_STATES.indexOf(current));
    index = Math.min(SHEET_STATES.length - 1, Math.max(0, index + direction));
    setSheetState(workspace, SHEET_STATES[index]);
  }
  function toggleSheet(workspace) {
    var current = clean(workspace.dataset.sheetState, 20) || 'collapsed',
      index = Math.max(0, SHEET_STATES.indexOf(current));
    setSheetState(workspace, SHEET_STATES[(index + 1) % SHEET_STATES.length]);
  }
  function bindSheetDrag(workspace) {
    var sheet = one(workspace, '[data-gproperty-map-sheet]'),
      handle = one(workspace, '[data-gproperty-map-sheet-toggle]');
    if (!sheet || !handle) return;
    var startY = 0,
      active = false;
    handle.addEventListener(
      'touchstart',
      function (event) {
        if (!isMobile() || !event.touches.length) return;
        active = true;
        startY = event.touches[0].clientY;
        workspace.dataset.sheetDragging = 'true';
      },
      { passive: true }
    );
    handle.addEventListener(
      'touchmove',
      function (event) {
        if (!active || !event.touches.length) return;
        var offset = Math.max(-96, Math.min(120, event.touches[0].clientY - startY));
        sheet.style.transform = 'translateY(' + offset + 'px)';
      },
      { passive: true }
    );
    handle.addEventListener(
      'touchend',
      function (event) {
        if (!active) return;
        active = false;
        var endY =
          event.changedTouches && event.changedTouches[0]
            ? event.changedTouches[0].clientY
            : startY;
        var delta = startY - endY;
        sheet.style.removeProperty('transform');
        delete workspace.dataset.sheetDragging;
        if (delta > 60) stepSheet(workspace, 1);
        else if (delta < -100) setSheetState(workspace, 'collapsed');
        else if (delta < -60) stepSheet(workspace, -1);
        else setSheetState(workspace, workspace.dataset.sheetState || 'collapsed');
        invalidateMap(workspace, 320);
      },
      { passive: true }
    );
    handle.addEventListener(
      'touchcancel',
      function () {
        active = false;
        sheet.style.removeProperty('transform');
        delete workspace.dataset.sheetDragging;
        invalidateMap(workspace, 0);
      },
      { passive: true }
    );
    sheet.addEventListener('transitionend', function (event) {
      if (event.propertyName === 'height') invalidateMap(workspace, 0);
    });
  }

  function bindLayerPanelDrag(workspace) {
    var panel = one(workspace, '[data-gproperty-map-layer-panel]');
    if (!panel) return;
    var startY = 0,
      active = false;
    panel.addEventListener(
      'touchstart',
      function (event) {
        if (!isMobile() || panel.hidden || !event.touches.length) return;
        var touch = event.touches[0],
          rect = panel.getBoundingClientRect();
        if (touch.clientY - rect.top > 56) return;
        active = true;
        startY = touch.clientY;
        panel.dataset.dragging = 'true';
        panel.style.transition = 'none';
      },
      { passive: true }
    );
    panel.addEventListener(
      'touchmove',
      function (event) {
        if (!active || !event.touches.length) return;
        var offset = Math.max(0, Math.min(180, event.touches[0].clientY - startY));
        panel.style.transform = 'translateY(' + offset + 'px)';
      },
      { passive: true }
    );
    panel.addEventListener(
      'touchend',
      function (event) {
        if (!active) return;
        active = false;
        var endY =
          event.changedTouches && event.changedTouches[0]
            ? event.changedTouches[0].clientY
            : startY;
        var delta = endY - startY;
        delete panel.dataset.dragging;
        panel.style.transition = 'transform 160ms ease';
        panel.style.removeProperty('transform');
        window.setTimeout(function () {
          panel.style.removeProperty('transition');
        }, 180);
        if (delta > 80) setLayerPanelOpen(workspace, false);
      },
      { passive: true }
    );
    panel.addEventListener(
      'touchcancel',
      function () {
        active = false;
        delete panel.dataset.dragging;
        panel.style.removeProperty('transform');
        panel.style.removeProperty('transition');
      },
      { passive: true }
    );
  }

  function syncNearbyControls(workspace, hasSelection) {
    if (!workspace) return;
    var enabled =
      hasSelection == null ? !!clean(workspace.dataset.selectedEstate, 160) : !!hasSelection;
    all(workspace, '[data-gproperty-map-nearby-type]').forEach(function (button) {
      button.disabled = !enabled;
      button.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      button.title = enabled ? '' : '請先選擇一個地點';
      if (!enabled) {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      }
    });
  }

  function renderNearbyList(workspace) {
    var list = one(workspace, '[data-gproperty-map-nearby-list]');
    if (!list) return 0;
    list.replaceChildren();
    var activeTypes = all(workspace, '[data-gproperty-map-nearby-type][aria-pressed="true"]')
      .map(function (button) {
        return clean(button.dataset.gpropertyMapNearbyType, 30);
      })
      .filter(Boolean);
    var total = 0;
    activeTypes.forEach(function (type) {
      var rows =
        workspace.__nearbyRows && Array.isArray(workspace.__nearbyRows[type])
          ? workspace.__nearbyRows[type]
          : [];
      rows.forEach(function (row) {
        var li = document.createElement('li');
        var name = clean(row.name_zh || row.name_en || row.name || row.label, 160);
        if (!name) return;
        var distance = finite(row.distance_m);
        var source = clean(row.source || row.provider || row.source_provider, 80);
        li.dataset.gpropertyMapNearbyResultType = type;
        li.textContent =
          name +
          (distance !== null ? ' · ' + Math.round(distance) + ' m' : '') +
          (source ? ' · ' + source : '');
        list.appendChild(li);
        total += 1;
      });
    });
    return total;
  }

  function clearNearby(workspace, status, type) {
    if (!workspace) return;
    workspace.__nearbyRequests = workspace.__nearbyRequests || Object.create(null);
    workspace.__nearbyRows = workspace.__nearbyRows || Object.create(null);
    var types = type
      ? [type]
      : Array.from(
          new Set(
            Object.keys(workspace.__nearbyRequests || {}).concat(
              Object.keys(workspace.__nearbyRows || {}),
              Object.keys(workspace.__contextLayers || {})
            )
          )
        );
    types.forEach(function (key) {
      var requestState = workspace.__nearbyRequests[key];
      if (requestState && requestState.controller) requestState.controller.abort();
      delete workspace.__nearbyRequests[key];
      delete workspace.__nearbyRows[key];
      clearContextLayer(workspace, key);
      var button = one(workspace, '[data-gproperty-map-nearby-type="' + key + '"]');
      if (button) {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      }
    });
    var total = renderNearbyList(workspace);
    setText(
      one(workspace, '[data-gproperty-map-nearby-status]'),
      status || (total ? total + ' 項' : '選擇周邊配套後載入')
    );
  }

  function activeNearbyLayer(config, type) {
    var layer =
      config.nearbyRegistry && config.nearbyRegistry.byType
        ? config.nearbyRegistry.byType[type]
        : null;
    if (!layer) return null;
    if (layer.renderMode !== 'nearby-list' && layer.renderMode !== 'nearby-list-map') return null;
    return layer;
  }

  function loadNearby(type, trigger) {
    var config = window.__gpropertyMapConfig,
      workspace = one(document, WORKSPACE_SELECTOR);
    if (!config || !workspace) return Promise.resolve([]);
    var layer = activeNearbyLayer(config, type),
      place = workspace.__selectedPlace || null,
      estateId = clean(workspace.dataset.selectedEstate, 160),
      lat = finite(place && place.lat),
      lng = finite(place && place.lng);
    if (!layer || !estateId || !place || !validCoordinates(lat, lng)) {
      setText(
        one(workspace, '[data-gproperty-map-nearby-status]'),
        !layer ? '此周邊配套類型尚未配置' : '請先選擇屋苑或地點'
      );
      if (trigger) {
        trigger.setAttribute('aria-pressed', 'false');
        trigger.classList.remove('is-active');
      }
      return Promise.resolve([]);
    }

    var surfaceTier =
      clean(config.nearbyRegistry && config.nearbyRegistry.surfaceTier, 20).toLowerCase() || 'pro';
    var availableOn = Array.isArray(layer.availableOn) ? layer.availableOn : ['free', 'pro'];

    if (availableOn.indexOf(surfaceTier) === -1) {
      setText(one(workspace, '[data-gproperty-map-nearby-status]'), '此圖層僅在 Pro 版本中提供');
      if (trigger) {
        trigger.setAttribute('aria-pressed', 'false');
        trigger.classList.remove('is-active');
      }
      return Promise.resolve([]);
    }

    var endpoint = layer.apiFamily === 'school-net' ? config.schoolNet : config.nearby;
    if (!endpoint) {
      setText(
        one(workspace, '[data-gproperty-map-nearby-status]'),
        '此周邊配套類型尚未配置可核實資料來源'
      );
      return Promise.resolve([]);
    }

    workspace.__nearbyRequests = workspace.__nearbyRequests || Object.create(null);
    workspace.__nearbyRows = workspace.__nearbyRows || Object.create(null);
    var previousRequest = workspace.__nearbyRequests[type];
    if (previousRequest && previousRequest.controller) previousRequest.controller.abort();
    var requestState = { controller: null, timedOut: false };
    workspace.__nearbyRequests[type] = requestState;
    var kidNearbyContext = mapboxRendererContext(workspace);
    var nearbyLimit = kidNearbyContext ? 8 : 12;
    var requestEstate = estateId,
      url = absoluteUrl(endpoint);
    url.searchParams.set('place_id', estateId);
    url.searchParams.set('lat', lat.toFixed(6));
    url.searchParams.set('lng', lng.toFixed(6));
    if (layer.apiFamily !== 'school-net') url.searchParams.set('types', layer.nearbyType);
    url.searchParams.set('radius_m', '1500');
    url.searchParams.set('limit', String(nearbyLimit));
    url.searchParams.set('tier', surfaceTier);

    setText(one(workspace, '[data-gproperty-map-nearby-status]'), '載入中…');
    return requestJson(url.toString(), requestState, 15000)
      .then(function (data) {
        if (
          !workspace.isConnected ||
          workspace.dataset.selectedEstate !== requestEstate ||
          workspace.__nearbyRequests[type] !== requestState
        ) {
          return [];
        }

        const hasItems = data && Array.isArray(data.items);
        const hasResults = data && Array.isArray(data.results);

        if (!hasItems && !hasResults) {
          throw new Error('Invalid nearby response: missing result list');
        }

        const sourceRows = (hasItems ? data.items : data.results).slice(0, nearbyLimit);

        const rows = sourceRows
          .map(function (row) {
            return normalizeNearbyFeature(row, type, place);
          })
          .filter(Boolean);

        if (sourceRows.length > 0 && rows.length === 0) {
          throw new Error('Invalid nearby records: no usable name and coordinates');
        }

        if (kidNearbyContext) {
          rows.sort(function (left, right) {
            var leftDistance = finite(left.distance_m);
            var rightDistance = finite(right.distance_m);
            if (leftDistance === null && rightDistance !== null) return 1;
            if (leftDistance !== null && rightDistance === null) return -1;
            if (leftDistance !== null && rightDistance !== null && leftDistance !== rightDistance) {
              return leftDistance - rightDistance;
            }
            return clean(left.name_zh || left.name_en, 160).localeCompare(
              clean(right.name_zh || right.name_en, 160)
            );
          });
        }

        workspace.__nearbyRows[type] = rows;

        if (kidNearbyContext && kidNearbyContext.root) {
          kidNearbyContext.root.setAttribute('data-kid-nearby-count', String(rows.length));
          kidNearbyContext.root.setAttribute(
            'data-kid-nearby-density',
            rows.length >= 3 ? 'curated' : 'sparse'
          );
        }

        renderNearbyMarkers(workspace, type, rows);
        const total = renderNearbyList(workspace);

        const pending = Object.keys(workspace.__nearbyRequests || {}).some(function (key) {
          return key !== type && Boolean(workspace.__nearbyRequests[key]);
        });

        const activeTypes = all(workspace, '[data-gproperty-map-nearby-type][aria-pressed="true"]')
          .map(function (button) {
            return clean(button.dataset.gpropertyMapNearbyType, 30);
          })
          .filter(Boolean);

        const incomplete = activeTypes.some(function (key) {
          return !Array.isArray(workspace.__nearbyRows[key]);
        });

        let message;

        if (pending) {
          message = '載入中…';
        } else if (incomplete) {
          message = '部分配套資料未能載入';
        } else {
          message = total ? total + ' 項' : '暫無已核實配套資料';
        }

        setText(one(workspace, '[data-gproperty-map-nearby-status]'), message);

        return rows;
      })
      .catch(function (error) {
        if (
          !workspace.isConnected ||
          workspace.dataset.selectedEstate !== requestEstate ||
          workspace.__nearbyRequests[type] !== requestState
        ) {
          return [];
        }

        if (error && error.name === 'AbortError' && !requestState.timedOut) {
          return [];
        }

        delete workspace.__nearbyRows[type];
        clearContextLayer(workspace, type);
        const total = renderNearbyList(workspace);

        const pending = Object.keys(workspace.__nearbyRequests || {}).some(function (key) {
          return key !== type && Boolean(workspace.__nearbyRequests[key]);
        });

        const dataNotReady =
          error &&
          error.httpStatus === 503 &&
          (error.apiStatus === 'data_not_ready' ||
            error.apiStatus === 'data_source_not_ready' ||
            error.code === 'verified_family_data_unavailable');

        const unauthorized = error && (error.httpStatus === 401 || error.httpStatus === 403);

        let message;

        if (pending) {
          message = '載入中…';
        } else if (total > 0) {
          message = '部分配套資料未能載入';
        } else if (dataNotReady) {
          message = '配套資料來源尚未就緒';
        } else if (unauthorized) {
          message = '無權限載入配套資料';
        } else {
          message = '暫時未能載入配套資料';
        }

        setText(one(workspace, '[data-gproperty-map-nearby-status]'), message);

        return [];
      })
      .finally(function () {
        if (workspace.__nearbyRequests[type] === requestState)
          delete workspace.__nearbyRequests[type];
      });
  }

  function renderLayerSwitcher(workspace, config) {
    var panel = one(workspace, '[data-gproperty-map-layer-panel]'),
      list = one(workspace, '[data-gproperty-map-layer-list]');
    if (!panel || !list) return;
    list.replaceChildren();
    (config.nearbyRegistry.layers || []).forEach(function (layer) {
      if (layer.id === 'price' && workspace.closest && workspace.closest('[data-littleg-kid-map]'))
        return;
      var button = document.createElement('button'),
        label = document.createElement('span'),
        note = document.createElement('small');
      button.type = 'button';
      button.className = 'gproperty-map-layer-thumb';
      button.dataset.gpropertyMapLayerId = layer.id;
      button.dataset.gpropertyMapLayerNearbyType = layer.nearbyType || '';
      var base = layer.id === 'base';
      var price = layer.id === 'price';
      var endpointReady =
        layer.nearbyType && (layer.apiFamily !== 'school-net' || !!config.schoolNet);
      var usable =
        base ||
        (price
          ? !!workspace.__publicPriceLayer
          : !!workspace.dataset.selectedEstate &&
            !!activeNearbyLayer(config, layer.nearbyType) &&
            endpointReady);
      var active = base || (price && publicPriceLayerVisible(workspace));
      button.disabled = !usable;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.classList.toggle('is-active', active);
      label.textContent =
        document.documentElement.lang.indexOf('zh') === 0 ? layer.labelZh : layer.labelEn;
      note.textContent = base ? '目前底圖' : price ? '' : usable ? '查看周邊' : '選擇地點後查看';
      button.appendChild(label);
      button.appendChild(note);
      list.appendChild(button);
    });
    syncPublicPriceLayerControl(workspace);
  }

  function setLayerPanelOpen(workspace, open) {
    var trigger = one(workspace, '[data-gproperty-map-layer-trigger]'),
      panel = one(workspace, '[data-gproperty-map-layer-panel]');
    if (!trigger || !panel) return;
    setHidden(panel, !open);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  function toggleLayerPanel(workspace) {
    var panel = one(workspace, '[data-gproperty-map-layer-panel]');
    if (!panel) return;
    setLayerPanelOpen(workspace, panel.hidden);
  }

  function projectSelectionMedia(workspace, place) {
    var image = one(workspace, '[data-gproperty-map-selection-image]'),
      placeholder = one(workspace, '[data-gproperty-map-selection-placeholder]'),
      gallery = one(workspace, '[data-gproperty-map-gallery]'),
      directions = one(workspace, '[data-gproperty-map-directions]'),
      transit = one(workspace, '[data-gproperty-map-transit-time]'),
      save = one(workspace, '[data-gproperty-map-action-save]');
    if (image) {
      if (place.image_url) {
        image.src = place.image_url;
        image.alt = place.name_zh;
        setHidden(image, false);
        setHidden(placeholder, true);
      } else {
        image.removeAttribute('src');
        image.alt = '';
        setHidden(image, true);
        setHidden(placeholder, false);
      }
    }
    if (gallery) {
      gallery.dataset.galleryUrl = place.gallery_url || '';
      setHidden(gallery, !place.gallery_url);
    }
    if (directions) {
      directions.href =
        'https://www.google.com/maps/dir/?api=1&destination=' +
        encodeURIComponent(place.lat + ',' + place.lng);
      setHidden(directions, false);
    }
    setText(transit, place.transit_time || '');
    if (save) {
      const config = window.__gpropertyMapConfig;
      const readEnabled = !config || config.savedPlaceRead !== false;
      const writeEnabled = !config || config.savedPlaceWrite !== false;
      const visible = readEnabled || writeEnabled;

      setHidden(save, !visible);
      save.disabled = !writeEnabled;

      if (!visible || !readEnabled) {
        // 不讀取收藏，也不把未知狀態顯示成「未收藏」。
        save.removeAttribute('aria-pressed');

        setText(one(save, 'span:last-child'), savedPlaceText('儲存於本機', 'Save locally'));
      } else {
        updateSavedPlaceButton(save, isSavedPlace(place));
      }
    }
  }

  function focusSelectedPlace(workspace, place) {
    if (!workspace || !workspace.__map || !place) return;

    var reduced = !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );

    var rendererContext = mapboxRendererContext(workspace);

    if (rendererContext && typeof rendererContext.renderer.flyTo === 'function') {
      var renderer = rendererContext.renderer;

      var zoom =
        typeof renderer.getZoom === 'function'
          ? Math.max(Number(renderer.getZoom(rendererContext.root)) || 0, 15)
          : 15;

      renderer.flyTo(rendererContext.root, {
        lat: place.lat,
        lng: place.lng,
        zoom: zoom,
      });

      if (!isMobile()) return;

      window.requestAnimationFrame(function () {
        var sheet = one(workspace, '[data-gproperty-map-sheet]');

        if (!workspace.__map || workspace.__selectedPlace !== place || !sheet || sheet.hidden) {
          return;
        }

        var sheetHeight = Math.max(0, Math.round(sheet.getBoundingClientRect().height));

        var offset = Math.min(120, Math.round(sheetHeight / 2));

        if (offset > 0 && typeof renderer.panBy === 'function') {
          renderer.panBy(rendererContext.root, {
            x: 0,
            y: offset,
            duration: reduced ? 0 : 250,
          });
        }
      });

      return;
    }

    var map = workspace.__map;

    var leafletZoom = Math.max(map.getZoom(), 15);

    map.setView([place.lat, place.lng], leafletZoom, {
      animate: !reduced,
    });

    if (!isMobile()) return;

    window.requestAnimationFrame(function () {
      var sheet = one(workspace, '[data-gproperty-map-sheet]');

      if (!workspace.__map || workspace.__selectedPlace !== place || !sheet || sheet.hidden) {
        return;
      }

      var sheetHeight = Math.max(0, Math.round(sheet.getBoundingClientRect().height));

      var offset = Math.min(120, Math.round(sheetHeight / 2));

      if (offset > 0 && typeof workspace.__map.panBy === 'function') {
        workspace.__map.panBy([0, offset], {
          animate: !reduced,
          duration: 0.25,
        });
      }
    });
  }

  function clearSelectedPlace(workspace, config) {
    if (!workspace || !clean(workspace.dataset.selectedEstate, 160)) return false;
    var previous = workspace.__selectedPlace || null,
      sheet = one(workspace, '[data-gproperty-map-selection]'),
      nextState = workspace.__items && workspace.__items.length ? 'ready' : 'idle';
    workspace.__selectedPlace = null;
    workspace.dataset.selectedEstate = '';
    workspace.dataset.sheetVisible = 'false';
    workspace.dataset.sheetState = 'collapsed';
    if (sheet) {
      sheet.dataset.sheetState = 'collapsed';
      setSheetContentState(one(sheet, '[data-gproperty-map-sheet-content]'), false);
      setHidden(sheet, true);
    }
    all(workspace, '[data-gproperty-map-result-index]').forEach(function (button) {
      button.classList.remove('is-selected');
      button.setAttribute('aria-pressed', 'false');
      button.removeAttribute('aria-current');
    });
    clearNearby(workspace, '請先選擇地點以檢視周邊配套');
    syncNearbyControls(workspace, false);
    clearMarkers(workspace);
    (workspace.__items || []).forEach(function (item) {
      addMarker(workspace, item, false);
    });
    syncPublicPriceSelection(workspace, '');
    renderLayerSwitcher(workspace, config);
    clearDraftSelection(config);
    updateWorkspaceState(
      workspace,
      nextState,
      nextState === 'ready' ? '已取消選擇地點；可重新選擇搜尋結果。' : '',
      ''
    );
    invalidateMap(workspace, 180);
    emitMapEvent(
      config,
      'gproperty:estate:cleared',
      {
        previous: previous,
      },
      {}
    );
    return true;
  }

  function selectPlace(place) {
    var config = window.__gpropertyMapConfig,
      searchRoot = one(document, SEARCH_SELECTOR),
      workspace = one(document, WORKSPACE_SELECTOR);
    if (!config || !workspace || !place) return;
    var previous = workspace.dataset.selectedEstate;
    workspace.__selectedPlace = place;
    workspace.dataset.selectedEstate = place.estate_id || place.key;
    workspace.dataset.state = 'selected';
    syncNearbyControls(workspace, true);
    if (previous !== workspace.dataset.selectedEstate) {
      all(workspace, '[data-gproperty-map-nearby-type]').forEach(function (button) {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      clearNearby(workspace, '選擇周邊配套後載入');
    }
    setHidden(one(workspace, '[data-gproperty-map-selection]'), false);
    setText(
      one(workspace, '[data-gproperty-map-selected-name]'),
      place.name_zh + (place.name_en ? ' · ' + place.name_en : '')
    );
    setText(one(workspace, '[data-gproperty-map-selected-address]'), place.address, '沒有返回地址');
    setText(
      one(workspace, '[data-gproperty-map-selected-district]'),
      place.district_code,
      '未返回'
    );
    setText(one(workspace, '[data-gproperty-map-selected-source]'), place.source, '未返回');
    setText(one(workspace, '[data-gproperty-map-selected-updated]'), place.updated_at, '未返回');
    setText(one(workspace, '[data-gproperty-map-selected-id]'), place.estate_id, '臨時地點記錄');
    var priceNode = one(workspace, '[data-gproperty-map-selected-price]');
    setText(priceNode, place.__isPublicPrice ? place.price_label + ' · 放盤叫價' : '');
    setHidden(priceNode, !place.__isPublicPrice);
    projectSelectionMedia(workspace, place);
    all(workspace, '[data-gproperty-map-result-index]').forEach(function (button) {
      var item = workspace.__items[Number(button.dataset.gpropertyMapResultIndex)],
        selected = item && item.key === place.key;
      button.classList.toggle('is-selected', !!selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      if (selected) button.setAttribute('aria-current', 'location');
      else button.removeAttribute('aria-current');
    });
    clearMarkers(workspace);
    (workspace.__items.length ? workspace.__items : place.__isPublicPrice ? [] : [place]).forEach(
      function (item) {
        addMarker(workspace, item, item.key === place.key);
      }
    );
    syncPublicPriceSelection(workspace, place.key);
    setSheetState(workspace, isMobile() ? 'collapsed' : 'expanded');

    focusSelectedPlace(workspace, place);

    writeDraft(config, place);

    saveSearchHistory(place.name_zh);

    renderLayerSwitcher(workspace, config);

    updateWorkspaceState(workspace, 'selected', '已選擇 ' + place.name_zh + '。', '');

    closeSuggestions(searchRoot);

    emitMapEvent(config, 'gproperty:estate:selected', place, {
      place_id: clean(place.estate_id, 120),

      source: clean(place.source, 80),
    });
  }

  // 解析經緯度座標 (如 "22.2855, 114.1577")
  function parseCoordinatesQuery(query) {
    var raw = clean(query, 120);
    var match = raw.match(/^\s*(-?\d+(?:\.\d+)?)\s*[\s,]\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (!match) return null;
    var lat = finite(match[1]),
      lng = finite(match[2]);
    return validCoordinates(lat, lng) ? { lat: lat, lng: lng } : null;
  }

  function searchLocations(query, suggestionsOnly) {
    var config = window.__gpropertyMapConfig,
      searchRoot = one(document, SEARCH_SELECTOR),
      workspace = one(document, WORKSPACE_SELECTOR),
      term = clean(query, 120);
    if (!config || !searchRoot || !workspace) return Promise.resolve([]);
    var queryState = setQueryState(searchRoot, term);
    if (queryState !== 'ready') {
      closeSuggestions(searchRoot);
      updateSearchState(
        searchRoot,
        'idle',
        queryState === 'short' ? '再輸入一個字開始搜尋。' : '',
        ''
      );
      return Promise.resolve([]);
    }
    if (!suggestionsOnly) setResultsPanelState(workspace, 'open');

    // 1. 座標直接解析與定位
    var coords = parseCoordinatesQuery(term);
    if (coords) {
      var coordPlace = {
        key: 'coord_' + coords.lat.toFixed(6) + '_' + coords.lng.toFixed(6),
        estate_id: '',
        name_zh: '座標定位 (' + coords.lat.toFixed(4) + ', ' + coords.lng.toFixed(4) + ')',
        name_en: 'Coordinates (' + coords.lat.toFixed(4) + ', ' + coords.lng.toFixed(4) + ')',
        address: coords.lat.toFixed(6) + ', ' + coords.lng.toFixed(6),
        district_code: 'GPS',
        lat: coords.lat,
        lng: coords.lng,
        source: 'coordinate-input',
        source_ref: '',
        updated_at: new Date().toISOString(),
        dataset_version: '1.0',
        confidence: 1.0,
        price_label: '',
        image_url: '',
        gallery_url: '',
        transit_time: '',
      };

      if (suggestionsOnly) {
        renderSuggestions(searchRoot, [coordPlace]);
        updateSearchState(searchRoot, 'success', '已解析座標。', '');
      } else {
        saveSearchHistory(term);
        renderResults(workspace, [coordPlace], true);
        selectPlace(coordPlace);
        closeSuggestions(searchRoot);
        updateSearchState(searchRoot, 'success', '已跳轉至指定座標位置。', '');
        updateWorkspaceState(workspace, 'success', '已跳轉至指定座標位置。', '');
      }
      return Promise.resolve([coordPlace]);
    }

    // 2. 取消未完成的請求
    if (searchRoot.__locationRequest && searchRoot.__locationRequest.controller)
      searchRoot.__locationRequest.controller.abort();
    searchRoot.__locationRequest = { controller: null, timedOut: false };

    if (suggestionsOnly) renderSuggestions(searchRoot, []);
    else renderResults(workspace, [], false);

    // 3. API 請求帶上當前 surfaceTier
    var url = absoluteUrl(config.locationSearch);
    url.searchParams.set('q', term);
    url.searchParams.set('lang', document.documentElement.lang || 'zh-Hant');
    url.searchParams.set('limit', suggestionsOnly ? '8' : '50');
    url.searchParams.set('mode', suggestionsOnly ? 'suggest' : 'search');
    var surfaceTier =
      clean(config.nearbyRegistry && config.nearbyRegistry.surfaceTier, 20).toLowerCase() || 'pro';
    url.searchParams.set('tier', surfaceTier);

    updateSearchState(searchRoot, 'loading', '正在搜尋真實地點資料…', '');
    if (!suggestionsOnly) updateWorkspaceState(workspace, 'loading', '正在搜尋真實地點資料…', '');

    return requestJson(url.toString(), searchRoot.__locationRequest, 15000)
      .then(function (data) {
        var places = normalizePlaces(data),
          state = places.length ? 'success' : 'empty';
        if (suggestionsOnly) {
          renderSuggestions(searchRoot, places);
          updateSearchState(
            searchRoot,
            state,
            places.length ? '請選擇建議地點。' : '沒有找到可驗證的地點。',
            ''
          );
        } else {
          saveSearchHistory(term);
          renderResults(workspace, places, true);
          closeSuggestions(searchRoot);
          var status = places.length
            ? '已返回 ' + places.length + ' 個地點。'
            : '沒有找到可驗證的地點。';
          updateSearchState(searchRoot, state, status, '');
          updateWorkspaceState(workspace, state, status, '');
        }
        projectKidSearchNetworkState(workspace, 'online');
        window.dispatchEvent(
          new CustomEvent('gproperty:search:completed', {
            detail: { term: term, places: places, suggestionsOnly: suggestionsOnly },
          })
        );
        return places;
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError' && !searchRoot.__locationRequest.timedOut)
          return [];
        var offline = error && error.message === 'offline',
          invalidQuery = error && error.httpStatus === 422,
          status = searchRoot.__locationRequest.timedOut
            ? '地點搜尋逾時，請稍後再試。'
            : offline
              ? '目前離線。真實地點搜尋需要網絡。'
              : invalidQuery
                ? '搜尋條件無效，請修改後再試。'
                : '暫時未能取得可驗證的地點資料。';
        closeSuggestions(searchRoot);
        projectKidSearchNetworkState(
          workspace,
          offline ? 'offline' : searchRoot.__locationRequest.timedOut ? 'timeout' : 'error'
        );
        updateSearchState(searchRoot, offline ? 'offline' : 'error', status, status);
        if (!suggestionsOnly)
          updateWorkspaceState(workspace, offline ? 'offline' : 'error', status, status);
        emitMapEvent(
          config,
          'gproperty:search:error',
          {
            term: term,
            error: error,
          },
          {
            error_code: clean(error && (error.code || failureMessage(error)), 120),

            suggestionsOnly: suggestionsOnly,
          }
        );
        return [];
      });
  }

  function clearLocation(workspace) {
    if (!workspace || !workspace.__map) return;

    var context = mapboxRendererContext(workspace);

    if (context && typeof context.renderer.clearUserLocation === 'function') {
      context.renderer.clearUserLocation(context.root);

      workspace.__locationMarker = null;
      workspace.__accuracyCircle = null;
      return;
    }

    if (workspace.__locationMarker) {
      try {
        workspace.__map.removeLayer(workspace.__locationMarker);
      } catch (_) {}

      workspace.__locationMarker = null;
    }

    if (workspace.__accuracyCircle) {
      try {
        workspace.__map.removeLayer(workspace.__accuracyCircle);
      } catch (_) {}

      workspace.__accuracyCircle = null;
    }
  }

  function renderLocation(workspace, lat, lng, accuracy) {
    if (!workspace || !workspace.__map) {
      return false;
    }

    clearLocation(workspace);

    workspace.__lastLocation = {
      lat: lat,
      lng: lng,
      accuracy: accuracy,
    };

    var context = mapboxRendererContext(workspace);

    if (context && typeof context.renderer.setUserLocation === 'function') {
      var rendered = context.renderer.setUserLocation(context.root, {
        lat: lat,
        lng: lng,
        accuracy: accuracy,
      });

      setHidden(one(workspace, '[data-gproperty-map-recenter]'), false);

      return rendered;
    }

    if (!window.L) {
      return false;
    }

    if (accuracy !== null && accuracy > 0) {
      workspace.__accuracyCircle = window.L.circle([lat, lng], {
        radius: Math.min(Math.max(accuracy, 5), 5000),
        interactive: false,
        className: 'gproperty-map-location-accuracy',
      }).addTo(workspace.__map);
    }

    workspace.__locationMarker = window.L.circleMarker([lat, lng], {
      radius: 8,
      weight: 4,
      className: 'gproperty-map-location-marker',
    }).addTo(workspace.__map);

    workspace.__locationMarker.bindPopup('目前位置');

    setHidden(one(workspace, '[data-gproperty-map-recenter]'), false);

    return true;
  }

  function projectKidLocationFeedback(workspace, state, message) {
    var context = mapboxRendererContext(workspace);
    if (!context || !context.root) return false;
    context.root.setAttribute('data-kid-location-state', state);
    var kid = window.LittleGKidMap;
    return !!(kid && typeof kid.notify === 'function' && kid.notify(message));
  }

  function useLocation(workspace) {
    var button = one(workspace, '[data-gproperty-map-use-location]');

    if (!navigator.geolocation) {
      updateWorkspaceState(workspace, 'error', '此裝置未提供定位功能。', '此裝置未提供定位功能。');
      projectKidLocationFeedback(workspace, 'unavailable', '此裝置未提供定位功能。');
      return;
    }

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    updateWorkspaceState(workspace, 'loading', '正在讀取裝置位置…', '');
    projectKidLocationFeedback(workspace, 'requesting', '正在取得目前位置…');

    try {
      navigator.geolocation.getCurrentPosition(
        function (position) {
          var lat = finite(position.coords && position.coords.latitude);

          var lng = finite(position.coords && position.coords.longitude);

          var accuracy = finite(position.coords && position.coords.accuracy);

          if (!validCoordinates(lat, lng) || !workspace.__map) {
            updateWorkspaceState(workspace, 'error', '未能讀取有效位置。', '未能讀取有效位置。');
            projectKidLocationFeedback(workspace, 'error', '未能讀取有效位置。');
          } else {
            renderLocation(workspace, lat, lng, accuracy);

            var context = mapboxRendererContext(workspace);

            if (context && typeof context.renderer.setView === 'function') {
              context.renderer.setView(context.root, {
                lat: lat,
                lng: lng,
                zoom: 16,
              });
            } else {
              workspace.__map.setView([lat, lng], 16, {
                animate: true,
              });
            }

            updateWorkspaceState(
              workspace,
              'ready',
              accuracy !== null
                ? '已顯示目前位置與約 ' + Math.round(accuracy) + ' 米精確度範圍。'
                : '已顯示目前位置。',
              ''
            );
            projectKidLocationFeedback(workspace, 'ready', '已定位到你附近。');
          }

          if (button) {
            button.disabled = false;
            button.setAttribute('aria-busy', 'false');
          }

          setMapSearchDisabled(workspace);
        },
        function (error) {
          var permissionDenied = !!(error && error.code === 1);

          var kidContext = mapboxRendererContext(workspace);

          var message = permissionDenied
            ? '定位未開啟。你仍可搜尋朋友或地點。'
            : error && error.code === 3
              ? '定位逾時。請稍後再試。'
              : '目前無法取得裝置位置。你仍可手動搜尋。';

          if (permissionDenied && kidContext) {
            kidContext.root.setAttribute('data-kid-location-state', 'denied');

            updateWorkspaceState(workspace, 'ready', message, '');
          } else {
            updateWorkspaceState(workspace, 'error', message, message);
          }

          projectKidLocationFeedback(
            workspace,
            permissionDenied ? 'denied' : error && error.code === 3 ? 'timeout' : 'error',
            message
          );
          if (button) {
            button.disabled = false;
            button.setAttribute('aria-busy', 'false');
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 9000,
          maximumAge: 60000,
        }
      );
    } catch (_) {
      if (button) {
        button.disabled = false;
        button.setAttribute('aria-busy', 'false');
      }
      updateWorkspaceState(workspace, 'error', '無法啟動裝置定位。', '無法啟動裝置定位。');
      projectKidLocationFeedback(workspace, 'error', '無法啟動裝置定位。');
    }
  }

  function recenter(workspace) {
    var location = workspace && workspace.__lastLocation;

    if (!workspace || !workspace.__map || !location) {
      updateWorkspaceState(workspace, 'error', '尚未取得目前位置。', '尚未取得目前位置。');
      return;
    }

    var context = mapboxRendererContext(workspace);

    if (context && typeof context.renderer.setView === 'function') {
      var zoom =
        typeof context.renderer.getZoom === 'function'
          ? Math.max(Number(context.renderer.getZoom(context.root)) || 0, 16)
          : 16;

      context.renderer.setView(context.root, {
        lat: location.lat,
        lng: location.lng,
        zoom: zoom,
      });

      return;
    }

    workspace.__map.setView([location.lat, location.lng], Math.max(workspace.__map.getZoom(), 16), {
      animate: true,
    });
  }

  function readUrlContext() {
    try {
      var params = new URLSearchParams(window.location.search);
      return {
        prompt: params.get('prompt') ? decodeURIComponent(params.get('prompt')) : '',
        layers: params.get('layers') ? params.get('layers').split(',') : [],
        workspace: clean(params.get('workspace') || '', 80),
      };
    } catch (_) {
      return { prompt: '', layers: [], workspace: '' };
    }
  }
  var LAYER_CHIP_MAP = { schools: 'school', isochrone_cross: 'mtr', school_net: 'school-net' };
  function applyUrlContext(workspace) {
    var ctx = readUrlContext();
    if (ctx.workspace) {
      var tab = one(workspace, '[data-gproperty-map-owner-view="' + ctx.workspace + '"]');
      if (tab && tab.getAttribute('aria-pressed') !== 'true') tab.click();
    }
    if (ctx.prompt) {
      var input = one(document, '[data-gproperty-map-search-input]');
      if (input) {
        input.value = ctx.prompt;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    ctx.layers.forEach(function (layer) {
      var type = LAYER_CHIP_MAP[clean(layer, 40)];
      if (!type) return;
      var chip = one(workspace, '[data-gproperty-map-nearby-type="' + type + '"]');
      if (chip && !chip.classList.contains('is-active')) chip.click();
    });
  }
  function updateVisualViewport(workspace) {
    var viewport = window.visualViewport,
      layoutHeight = Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0),
      height = Math.max(1, Math.round(viewport ? viewport.height : layoutHeight)),
      offsetTop = Math.max(0, Math.round(viewport ? viewport.offsetTop : 0));
    if (!workspace.__layoutViewportHeight || height > workspace.__layoutViewportHeight * 0.88)
      workspace.__layoutViewportHeight = Math.max(layoutHeight, height + offsetTop);
    var bottomInset = Math.max(
      0,
      Math.round(workspace.__layoutViewportHeight - height - offsetTop)
    );
    workspace.style.setProperty('--gpm-visual-viewport-height', height + 'px');
    workspace.style.setProperty('--gpm-visual-bottom-inset', bottomInset + 'px');
    workspace.dataset.keyboardOpen =
      bottomInset > 120 || height < workspace.__layoutViewportHeight * 0.72 ? 'true' : 'false';
    invalidateMap(workspace, 80);
  }
  function restoreDraft(config, workspace) {
    var place = normalizePlace(readDraft(config).estate_context || {}, 'local-draft');
    if (place) {
      renderResults(workspace, [place], true);
      selectPlace(place);
    }
  }

  function mountSearch(searchRoot) {
    if (!searchRoot || searchRoot.dataset.gpropertyMapReady === VERSION) return;
    searchRoot.dataset.gpropertyMapReady = VERSION;
    searchRoot.__suggestions = [];
    searchRoot.__suggestionIndex = -1;
    searchRoot.__locationRequest = null;
    var input = one(searchRoot, '[data-gproperty-map-search-input]'),
      form = one(searchRoot, '[data-gproperty-map-search-form]'),
      timer = 0,
      blurTimer = 0;
    setQueryState(searchRoot, input ? input.value : '');
    if (input) {
      input.addEventListener('focus', function () {
        window.clearTimeout(blurTimer);
        var workspace = one(document, WORKSPACE_SELECTOR);
        if (workspace && isMobile() && workspace.dataset.sheetVisible === 'true')
          setSheetState(workspace, 'collapsed');
        if (!clean(input.value, 120)) renderHistory(searchRoot);
      });
      input.addEventListener('blur', function () {
        blurTimer = window.setTimeout(function () {
          closeSuggestions(searchRoot);
        }, 150);
      });
      input.addEventListener('input', function () {
        window.clearTimeout(timer);
        var term = clean(input.value, 120),
          state = setQueryState(searchRoot, term);
        if (state === 'empty') {
          renderHistory(searchRoot);
          updateSearchState(searchRoot, 'idle', '', '');
          return;
        }
        if (state !== 'ready') {
          closeSuggestions(searchRoot);
          updateSearchState(searchRoot, 'idle', '再輸入一個字開始搜尋。', '');
          return;
        }
        timer = window.setTimeout(function () {
          searchLocations(term, true);
        }, 300);
      });
      input.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          moveSuggestion(searchRoot, searchRoot.__suggestionIndex + 1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          moveSuggestion(searchRoot, searchRoot.__suggestionIndex - 1);
        } else if (event.key === 'Enter' && searchRoot.__suggestionIndex >= 0) {
          event.preventDefault();
          selectPlace(searchRoot.__suggestions[searchRoot.__suggestionIndex]);
        } else if (event.key === 'Escape') closeSuggestions(searchRoot);
      });
    }
    if (form)
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        searchLocations(input ? input.value : '', false);
      });
    searchRoot.addEventListener('mousedown', function () {
      window.clearTimeout(blurTimer);
    });
    searchRoot.addEventListener('click', function (event) {
      var suggestion =
        event.target.closest && event.target.closest('[data-gproperty-map-suggestion-index]');
      if (suggestion && searchRoot.contains(suggestion)) {
        selectPlace(
          searchRoot.__suggestions[Number(suggestion.dataset.gpropertyMapSuggestionIndex)]
        );
        return;
      }
      var history =
        event.target.closest && event.target.closest('[data-gproperty-map-history-term]');
      if (history && searchRoot.contains(history)) {
        var term = clean(history.dataset.gpropertyMapHistoryTerm, 120);
        if (input) input.value = term;
        searchLocations(term, false);
      }
    });
    document.addEventListener('click', function (event) {
      if (!searchRoot.contains(event.target)) closeSuggestions(searchRoot);
    });
  }

  function mountWorkspace(workspace, config) {
    if (!workspace || workspace.dataset.gpropertyMapReady === VERSION) return;
    workspace.dataset.gpropertyMapReady = VERSION;
    workspace.dataset.runtimePlatform = isNative() ? 'capacitor' : 'pwa-web';
    workspace.dataset.nearbyContractRole = config.nearbyRegistry.role;
    workspace.dataset.nearbyRendersMapOverlay = config.nearbyRegistry.rendersMapOverlay
      ? 'true'
      : 'false';
    workspace.dataset.providerPriorityPolicy = config.providerPolicy.priority;
    workspace.dataset.providerContractValid = config.providerContractValid ? 'true' : 'false';
    workspace.__items = [];
    workspace.__nearbyRequests = Object.create(null);
    workspace.__nearbyRows = Object.create(null);
    workspace.__layoutViewportHeight = 0;
    setResultsPanelState(workspace, workspace.dataset.resultsPanelState || 'collapsed');
    setMapSearchDisabled(workspace);
    syncNearbyControls(workspace, false);
    renderLayerSwitcher(workspace, config);
    waitForMap(workspace, config);
    updateVisualViewport(workspace);
    bindSheetDrag(workspace);
    bindLayerPanelDrag(workspace);
    var resultsPanel = one(workspace, '[data-gproperty-map-results-panel]');
    if (resultsPanel)
      resultsPanel.addEventListener('transitionend', function (event) {
        if (event.propertyName === 'transform') invalidateMap(workspace, 0);
      });
    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        function () {
          updateVisualViewport(workspace);
        },
        { passive: true }
      );
      window.visualViewport.addEventListener(
        'scroll',
        function () {
          updateVisualViewport(workspace);
        },
        { passive: true }
      );
    }
    window.addEventListener(
      'resize',
      function () {
        updateVisualViewport(workspace);
        if (isMobile() && workspace.dataset.resultsPanelState !== 'open')
          setResultsPanelState(workspace, 'open');
      },
      { passive: true }
    );

    // The consent button dispatches once within the same trusted user gesture.
    if (workspace.hasAttribute('data-littleg-kid-map')) {
      workspace.addEventListener('littleg:kid-map:use-location', function () {
        useLocation(workspace);
      });
    }

    workspace.addEventListener('click', function (event) {
      var target = event.target.closest ? event.target : null;

      const presetButton = target && target.closest('[data-gcp-viewport-preset]');

      if (presetButton && workspace.contains(presetButton)) {
        if (!config.node || config.node.getAttribute('data-route-role') !== 'place-context') return;

        const isZh = (document.documentElement.lang || '').toLowerCase().startsWith('zh');

        const provider = window.LittleGCityMissionMapScene;
        const preset =
          provider && typeof provider.getPreset === 'function'
            ? provider.getPreset(presetButton.getAttribute('data-gcp-viewport-preset'))
            : null;

        if (
          !workspace.__map ||
          typeof workspace.__map.setView !== 'function' ||
          !preset ||
          !Array.isArray(preset.center) ||
          preset.center.length !== 2 ||
          !validCoordinates(preset.center[0], preset.center[1]) ||
          !Number.isInteger(preset.zoom) ||
          preset.zoom < 0 ||
          preset.zoom > config.baseMap.maxZoom
        ) {
          showGestureHint(workspace, isZh ? '概覽暫未可用' : 'Overview is unavailable');
          return;
        }

        const reduceMotion =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        workspace.__map.setView(preset.center.slice(), preset.zoom, { animate: !reduceMotion });

        showGestureHint(workspace, isZh ? '已切換至香港概覽' : 'Showing Hong Kong overview');
        return;
      }
      var resultsToggle = target && target.closest('[data-gproperty-map-results-toggle]');
      if (resultsToggle && workspace.contains(resultsToggle)) {
        toggleResultsPanel(workspace);
        return;
      }
      var sheetButton =
        target &&
        target.closest('[data-gproperty-map-sheet-toggle], [data-gproperty-map-sheet-step]');
      if (sheetButton && workspace.contains(sheetButton)) {
        toggleSheet(workspace);
        return;
      }
      var result = target && target.closest('[data-gproperty-map-result-index]');
      if (result && workspace.contains(result)) {
        selectPlace(workspace.__items[Number(result.dataset.gpropertyMapResultIndex)]);
        return;
      }
      var layerTrigger = target && target.closest('[data-gproperty-map-layer-trigger]');
      if (layerTrigger && workspace.contains(layerTrigger)) {
        toggleLayerPanel(workspace);
        return;
      }
      var layerClose = target && target.closest('[data-gproperty-map-layer-close]');
      if (layerClose && workspace.contains(layerClose)) {
        setLayerPanelOpen(workspace, false);
        return;
      }
      var layerButton = target && target.closest('[data-gproperty-map-layer-id]');
      if (layerButton && workspace.contains(layerButton)) {
        if (layerButton.dataset.gpropertyMapLayerId === 'price') {
          togglePublicPriceLayer(workspace);
          return;
        }
        var type = clean(layerButton.dataset.gpropertyMapLayerNearbyType, 40);
        if (type) {
          var chip = one(workspace, '[data-gproperty-map-nearby-type="' + type + '"]');
          if (chip) chip.click();
        }
        return;
      }
      var nearby = target && target.closest('[data-gproperty-map-nearby-type]');
      if (nearby && workspace.contains(nearby)) {
        var type = clean(nearby.dataset.gpropertyMapNearbyType, 30),
          activate = nearby.getAttribute('aria-pressed') !== 'true';
        nearby.classList.toggle('is-active', activate);
        nearby.setAttribute('aria-pressed', activate ? 'true' : 'false');
        if (activate) loadNearby(type, nearby);
        else clearNearby(workspace, '', type);
        return;
      }
      if (target && target.closest('[data-gproperty-map-use-location]')) {
        useLocation(workspace);
        return;
      }
      if (target && target.closest('[data-gproperty-map-recenter]')) {
        recenter(workspace);
        return;
      }
      if (target && target.closest('[data-gproperty-map-search-area]')) {
        if (!config.mapSearch) failClosedMapSearch(workspace);
        return;
      }
      if (target && target.closest('[data-gproperty-map-action-save]')) {
        toggleSavedPlace(workspace);
        return;
      }
      var gallery = target && target.closest('[data-gproperty-map-gallery]');
      if (gallery && workspace.contains(gallery)) {
        var galleryUrl = safeUrl(gallery.dataset.galleryUrl);
        if (galleryUrl) window.open(galleryUrl, '_blank', 'noopener');
        return;
      }
      if (target && target.closest('[data-gproperty-map-apply-filters]')) {
        failClosedMapSearch(workspace);
        return;
      }
      if (target && target.closest('[data-gproperty-map-reset-filters]')) {
        all(workspace, '[data-gproperty-map-filter]').forEach(function (field) {
          field.value = '';
        });
        updateWorkspaceState(workspace, 'idle', '已清除篩選；使用上方搜尋更新結果。', '');
      }
    });

    workspace.addEventListener('keydown', function (event) {
      var button =
        event.target.closest &&
        event.target.closest('[data-gproperty-map-sheet-toggle], [data-gproperty-map-sheet-step]');
      if (!button || !workspace.contains(button) || !isMobile()) return;
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        stepSheet(workspace, 1);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        stepSheet(workspace, -1);
      } else if (event.key === 'Home' || event.key === 'Escape') {
        event.preventDefault();
        setSheetState(workspace, 'collapsed');
      } else if (event.key === 'End') {
        event.preventDefault();
        setSheetState(workspace, 'expanded');
      }
    });
    restoreDraft(config, workspace);
  }

  function bindNetwork(searchRoot, workspace) {
    if (window.__GPROPERTY_MAP_NETWORK_BOUND__ === VERSION) return;
    window.__GPROPERTY_MAP_NETWORK_BOUND__ = VERSION;
    window.addEventListener('online', function () {
      searchRoot.classList.remove('is-offline');
      workspace.classList.remove('is-offline');
      updateSearchState(searchRoot, 'idle', '網絡已恢復。', '');
      updateWorkspaceState(workspace, 'idle', '網絡已恢復。', '');
    });
    window.addEventListener('offline', function () {
      updateSearchState(searchRoot, 'offline', '目前離線。真實地點搜尋暫不可用。', '');
      updateWorkspaceState(workspace, 'offline', '目前離線。真實地點搜尋暫不可用。', '');
    });
  }
  function mount() {
    var config = readConfig(),
      searchRoot = one(document, SEARCH_SELECTOR),
      workspace = one(document, WORKSPACE_SELECTOR);
    if (!config || !searchRoot || !workspace) return;
    window.__gpropertyMapConfig = config;
    mountSearch(searchRoot);
    mountWorkspace(workspace, config);
    bindNetwork(searchRoot, workspace);
  }

  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
  document.addEventListener('shopify:section:load', mount);
  window.GPropertyMapWorkspace = {
    mount: mount,
    version: VERSION,
    setPublicPriceListings: setPublicPriceListings,
  };
})(window, document);
